import React, { useState, useEffect, useCallback } from 'react';
import DashboardLayout from '../components/layouts/DashboardLayout';
import { formComponentMap } from './website-admin/forms';
import {
  Users,
  FileCheck,
  Calendar,
  Award,
  CreditCard,
  Settings,
  Tag,
  RefreshCw,
  Search,
  CheckCircle2,
  AlertCircle,
  Clock,
  Plus,
  Trash2,
  Edit3,
  ExternalLink,
  ShieldCheck,
  MapPin,
  ChevronRight,
  X,
  Sparkles,
  Heart,
  BookOpen,
  GraduationCap,
  School,
  FileSpreadsheet,
  Layers,
  Download,
  XCircle,
  MessageSquare,
  AlertTriangle,
  Lock,
  Eye,
  FileText,
  Printer,
  Info
} from 'lucide-react';
import {
  LetterOfAcceptanceModal,
  OfficialEnrollmentCertificateModal,
  OfficialPaymentReceiptModal,
  ExaminationPassModal
} from '../components/admissions/EnglishAdmissionsDocuments';
import '../styles/dashboard.css';
import ModernAdmissionsToast from '../components/admissions/ModernAdmissionsToast';
import {
  CurrencyInput,
  QuantityStepperInput,
  DevelopmentFeeTerminCalculator,
} from '../components/admissions/AdmissionsFormInputs';
import AssessmentCurriculumManager from './admissions-admin/AssessmentCurriculumManager';
import './admissions-admin/AdmissionsAdminDashboard.css';

const LEVEL_CARD_META = {
  preschool: {
    title: 'Preschool',
    subtitle: 'Toddler, KD1 & KD2',
    icon: Sparkles,
  },
  kindergarten: {
    title: 'Kindergarten',
    subtitle: 'KGA & KGB',
    icon: Heart,
  },
  primary: {
    title: 'Primary School',
    subtitle: 'Grade 1 - 6 (SD)',
    icon: BookOpen,
  },
  secondary: {
    title: 'Secondary School',
    subtitle: 'Grade 7 - 9 (SMP)',
    icon: GraduationCap,
  },
  sma: {
    title: 'High School (SMA)',
    subtitle: 'Grade 10 - 12',
    icon: School,
  },
};

const LEVEL_TABS = [
  { code: 'preschool', label: 'Preschool' },
  { code: 'kindergarten', label: 'Kindergarten' },
  { code: 'primary', label: 'Primary' },
  { code: 'secondary', label: 'Secondary' },
  { code: 'sma', label: 'High School (SMA)' },
];

const formatCurrency = (val) => {
  const num = Number(val || 0);
  return 'Rp ' + num.toLocaleString('id-ID');
};

const escapeXml = (unsafe) => {
  if (unsafe === null || unsafe === undefined) return '';
  return String(unsafe)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
};

const formatDateId = (dateStr) => {
  if (!dateStr) return '-';
  try {
    return new Date(dateStr).toLocaleDateString('id-ID', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch (_) {
    return dateStr;
  }
};

function AdmissionsAdminDashboard({ user, onLogout, apiBase }) {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [overview, setOverview] = useState({
    total_applicants: 0,
    by_status: {},
    by_level: {},
    by_campus: {},
    levels: [],
    periods: [],
    test_slots: [],
    promo_codes: [],
    recent_applications: [],
  });
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [feedback, setFeedback] = useState(null);

  // Applicant filters & selected applicant modal
  const [searchQuery, setSearchQuery] = useState('');
  const [filterAcademicYear, setFilterAcademicYear] = useState('2026/2027');
  const [filterPeriod, setFilterPeriod] = useState('');
  const [filterLevel, setFilterLevel] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterCampus, setFilterCampus] = useState('');
  const [selectedApp, setSelectedApp] = useState(null);
  const [detailSubTab, setDetailSubTab] = useState('docs'); // 'docs' | 'test' | 'payment'

  // Document Validation & Test Scheduling Form
  const [docDecisionForm, setDocDecisionForm] = useState({
    decision: 'approve',
    test_slot_id: '',
    custom_test_date: '',
    custom_test_time: '',
    custom_campus: '',
    custom_room: '',
    admin_note: '',
    documents: {},
  });
  const [submittingDocs, setSubmittingDocs] = useState(false);

  // Offline Test Result & Invoice Generation Form
  const [testResultForm, setTestResultForm] = useState({
    status: 'passed',
    score: '',
    evaluation_notes: '',
    test_result_doc_url: '',
    registration_fee: 0,
    test_reg_form_fee: 0,
    admission_fee: 0,
    development_fee: 0,
    books_activity_fee: 0,
    tuition_fee: 0,
    discount_amount: 0,
    discount_label: '',
  });
  const [uploadingTestDoc, setUploadingTestDoc] = useState(false);
  const [submittingTest, setSubmittingTest] = useState(false);

  // Payment Verification Form
  const [paymentVerifyForm, setPaymentVerifyForm] = useState({
    decision: 'verify',
    student_id_number: '',
    official_receipt_doc_url: '',
    admin_note: '',
  });
  const [uploadingReceiptDoc, setUploadingReceiptDoc] = useState(false);
  const [submittingVerify, setSubmittingVerify] = useState(false);

  // Period Form
  const [editingPeriod, setEditingPeriod] = useState(null);
  const [periodForm, setPeriodForm] = useState({
    period_name: '',
    academic_year: '2026/2027',
    start_date: '',
    end_date: '',
    quotas: { preschool: 25, kindergarten: 30, primary: 60, secondary: 45, sma: 0 },
    is_active: true,
    next_period_id: '',
    notes: '',
  });

  // Test Slot Form
  const [slotForm, setSlotForm] = useState({
    period_id: 1,
    level_code: 'all',
    session_name: '',
    test_date: '',
    test_time: '09:00 - 11:00 WIB',
    campus_location: 'Campus 1 (Pos Pengumben) - Jl. Pos Pengumben Lama No.13',
    room_name: 'Gedung Utama Lt. 2 - Ruang Assessment A',
    slot_quota: 15,
    custom_notes: '',
  });

  // Level Config & Fee Editor
  const [selectedLevelCfg, setSelectedLevelCfg] = useState(null);
  const [savingLevelCfg, setSavingLevelCfg] = useState(false);

  // Promo Code Form
  const [promoForm, setPromoForm] = useState({
    promo_code: '',
    promo_title: '',
    discount_type: 'fixed',
    discount_value: '',
    applies_to_levels: 'all',
    max_quota: 100,
    valid_until: '2027-06-30',
  });

  // Config Modals State (Clean Data Tables + Add Modals Workflow)
  const [isPeriodModalOpen, setIsPeriodModalOpen] = useState(false);
  const [isSavingPeriod, setIsSavingPeriod] = useState(false);
  const [isSlotModalOpen, setIsSlotModalOpen] = useState(false);
  const [isSavingSlot, setIsSavingSlot] = useState(false);
  const [editingSlot, setEditingSlot] = useState(null);
  const [isFeeModalOpen, setIsFeeModalOpen] = useState(false);
  const [feeModalTab, setFeeModalTab] = useState('fees'); // 'fees' | 'status_mat' | 'bank_sign'
  const [isPromoModalOpen, setIsPromoModalOpen] = useState(false);
  const [isSavingPromo, setIsSavingPromo] = useState(false);

  // Official Document Preview & Attendance Roster Modals State
  const [previewLoaApp, setPreviewLoaApp] = useState(null);
  const [previewCertApp, setPreviewCertApp] = useState(null);
  const [previewReceiptApp, setPreviewReceiptApp] = useState(null);
  const [previewTestCardApp, setPreviewTestCardApp] = useState(null);
  const [attendanceModalSlot, setAttendanceModalSlot] = useState(null);

  // Promo Code Usage History Modal State
  const [promoHistoryModal, setPromoHistoryModal] = useState(null);
  const [loadingPromoUsers, setLoadingPromoUsers] = useState(false);

  // Dedicated Detail Modals for Clean Table View
  const [detailPeriodModal, setDetailPeriodModal] = useState(null);
  const [detailSlotModal, setDetailSlotModal] = useState(null);
  const [detailPromoModal, setDetailPromoModal] = useState(null);
  const [detailPromoUsers, setDetailPromoUsers] = useState([]);
  const [loadingDetailPromoUsers, setLoadingDetailPromoUsers] = useState(false);

  // Print official attendance roster for a test slot
  const handlePrintAttendanceSheet = (slot, attendees) => {
    if (!slot) return;
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      alert('Mohon izinkan pop-up peramban untuk mencetak lembar presensi.');
      return;
    }

    const rowsHtml = attendees.length === 0
      ? `<tr><td colspan="7" style="text-align: center; padding: 24px; color: #64748b;">Belum ada calon siswa yang dijadwalkan pada sesi tes ini.</td></tr>`
      : attendees.map((att, idx) => `
        <tr>
          <td style="text-align: center; font-weight: bold; padding: 9px 6px; border: 1px solid #cbd5e1;">${idx + 1}</td>
          <td style="font-family: monospace; font-weight: bold; padding: 9px 8px; border: 1px solid #cbd5e1; font-size: 11px;">${att.registration_number || '-'}</td>
          <td style="padding: 9px 8px; border: 1px solid #cbd5e1;">
            <strong>${att.child_full_name}</strong>
            <div style="font-size: 10px; color: #64748b;">Pilihan Kampus: ${att.campus_choice || '-'}</div>
          </td>
          <td style="padding: 9px 8px; border: 1px solid #cbd5e1; text-align: center; font-weight: 600; font-size: 11px;">${(att.level_name || att.level_code || '').toUpperCase()}</td>
          <td style="padding: 9px 8px; border: 1px solid #cbd5e1; font-size: 11px;">
            <div>${att.parent_name || att.father_name || att.mother_name || '-'}</div>
            <div style="font-size: 10px; color: #64748b;">${att.parent_phone || att.whatsapp_number || '-'}</div>
          </td>
          <td style="height: 44px; border: 1px solid #cbd5e1; text-align: center; vertical-align: bottom; font-size: 10px; color: #94a3b8; padding-bottom: 3px;">
            ${idx + 1}. ..................................
          </td>
          <td style="border: 1px solid #cbd5e1;"></td>
        </tr>
      `).join('');

    const html = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Lembar Presensi Sesi Tes - ${slot.session_name}</title>
        <style>
          @page { size: A4 portrait; margin: 12mm 15mm; }
          body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif; color: #0f172a; margin: 0; padding: 0; font-size: 11px; }
          .header-table { width: 100%; border-collapse: collapse; margin-bottom: 12px; border-bottom: 2px solid #0f172a; padding-bottom: 10px; }
          .title { font-size: 15px; font-weight: 800; text-transform: uppercase; letter-spacing: 0.5px; margin: 0; }
          .subtitle { font-size: 11px; color: #475569; margin: 3px 0 0; }
          .meta-box { width: 100%; border-collapse: collapse; margin-bottom: 14px; background: #f8fafc; border: 1px solid #cbd5e1; }
          .meta-box td { padding: 6px 10px; font-size: 11px; }
          .main-table { width: 100%; border-collapse: collapse; margin-bottom: 20px; }
          .main-table th { background: #f1f5f9; color: #1e293b; font-weight: 700; padding: 7px 8px; border: 1px solid #cbd5e1; font-size: 10px; text-transform: uppercase; }
          .sign-section { width: 100%; margin-top: 20px; border-collapse: collapse; }
          .sign-section td { text-align: center; width: 50%; padding: 0 20px; font-size: 11px; }
          .sign-space { height: 55px; }
          @media print {
            body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          }
        </style>
      </head>
      <body>
        <table class="header-table">
          <tr>
            <td>
              <div class="title">SEKOLAH PERMATA HARAPAN</div>
              <div class="subtitle">Admissions &amp; Academic Assessment Office &bull; ${slot.campus_location}</div>
              <div style="font-size: 13px; font-weight: 700; color: #0284c7; margin-top: 5px;">
                LEMBAR PRESENSI &amp; DAFTAR HADIR TES SELEKSI / OBSERVASI OFFLINE
              </div>
            </td>
            <td style="text-align: right; vertical-align: top;">
              <div style="font-size: 10px; color: #64748b;">Dicetak: ${new Date().toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' })}</div>
              <div style="font-size: 10px; font-weight: bold; color: #0f172a; margin-top: 2px;">Status: Resmi &bull; Terdaftar</div>
            </td>
          </tr>
        </table>

        <table class="meta-box">
          <tr>
            <td style="width: 20%;"><strong>Nama Sesi Tes:</strong></td>
            <td style="width: 35%;">${slot.session_name}</td>
            <td style="width: 20%;"><strong>Ruangan &amp; Lokasi:</strong></td>
            <td style="width: 25%;">${slot.room_name || '-'} (${slot.campus_location.split(' - ')[0]})</td>
          </tr>
          <tr>
            <td><strong>Waktu Pelaksanaan:</strong></td>
            <td>${formatDateId(slot.test_date)}, ${slot.test_time}</td>
            <td><strong>Kapasitas Peserta:</strong></td>
            <td>${attendees.length} / ${slot.slot_quota} Peserta</td>
          </tr>
        </table>

        <table class="main-table">
          <thead>
            <tr>
              <th style="width: 30px; text-align: center;">No</th>
              <th style="width: 110px;">No. Registrasi</th>
              <th>Nama Lengkap Calon Siswa</th>
              <th style="width: 80px; text-align: center;">Jenjang</th>
              <th style="width: 140px;">Orang Tua / No. WA</th>
              <th style="width: 130px; text-align: center;">Tanda Tangan Hadir</th>
              <th style="width: 90px; text-align: center;">Skor / Catatan</th>
            </tr>
          </thead>
          <tbody>
            ${rowsHtml}
          </tbody>
        </table>

        <table class="sign-section">
          <tr>
            <td>
              <div>Pengawas / Guru Penguji Tes,</div>
              <div class="sign-space"></div>
              <div style="font-weight: bold;">( ................................................................ )</div>
            </td>
            <td>
              <div>Petugas Admin Admissions,</div>
              <div class="sign-space"></div>
              <div style="font-weight: bold;">( ................................................................ )</div>
            </td>
          </tr>
        </table>

        <script>
          window.onload = function() {
            window.print();
          }
        </script>
      </body>
      </html>
    `;

    printWindow.document.open();
    printWindow.document.write(html);
    printWindow.document.close();
  };

  const getAuthHeaders = useCallback((includeJson = true) => {
    const token = localStorage.getItem('ph_auth_token') || localStorage.getItem('auth_token');
    const headers = {};
    if (includeJson) {
      headers['Content-Type'] = 'application/json';
    }
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    return headers;
  }, []);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [ovRes, appsRes] = await Promise.all([
        fetch(`${apiBase}/admin/admissions/overview`, {
          credentials: 'include',
          headers: getAuthHeaders(),
        }),
        fetch(`${apiBase}/admin/admissions/applications`, {
          credentials: 'include',
          headers: getAuthHeaders(),
        }),
      ]);

      const ovData = await ovRes.json().catch(() => null);
      if (ovRes.ok && ovData?.ok && ovData.data) {
        setOverview(ovData.data);
        if (!selectedLevelCfg && ovData.data.levels?.length > 0) {
          setSelectedLevelCfg(ovData.data.levels[0]);
        } else if (selectedLevelCfg && ovData.data.levels) {
          const refreshedLvl = ovData.data.levels.find((l) => l.level_code === selectedLevelCfg.level_code);
          if (refreshedLvl) setSelectedLevelCfg(refreshedLvl);
        }
      }

      const appsData = await appsRes.json().catch(() => null);
      if (appsRes.ok && appsData?.ok) {
        setApplications(appsData.data || []);
      }
    } catch (err) {
      console.error('Error loading admissions admin data:', err);
    } finally {
      setLoading(false);
    }
  }, [apiBase, getAuthHeaders, selectedLevelCfg]);

  useEffect(() => {
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sync selectedApp when applications list refreshes
  useEffect(() => {
    if (selectedApp) {
      const updated = applications.find((a) => a.id === selectedApp.id);
      if (updated) {
        setSelectedApp(updated);
      }
    }
  }, [applications, selectedApp]);

  const openApplicantModal = (app, initialSubTab = 'docs') => {
    setSelectedApp(app);
    setDetailSubTab(initialSubTab);

    // Populate Doc Validation & Schedule Form
    setDocDecisionForm({
      decision: 'approve',
      test_slot_id: app.test_slot_id || '',
      custom_test_date: app.test_schedule?.test_date || '',
      custom_test_time: app.test_schedule?.test_time || '',
      custom_campus: app.test_schedule?.campus_location || app.campus_location || '',
      custom_room: app.test_schedule?.room_name || '',
      admin_note: app.admin_note || '',
      documents: app.documents || {},
    });

    // Populate Test Result & Fee Form from level defaults or existing invoice
    const lvl = overview.levels.find((l) => l.level_code === app.level_code) || {};
    const inv = app.invoice || {};
    const autoDiscount = app.discount_amount > 0 ? app.discount_amount : 0;

    setTestResultForm({
      status: app.test_result === 'failed' ? 'failed' : 'passed',
      score: app.test_score !== null && app.test_score !== undefined ? app.test_score : '',
      evaluation_notes: app.test_evaluation_notes || '',
      test_result_doc_url: app.test_result_doc_url || '',
      registration_fee: lvl.registration_fee ?? 0,
      test_reg_form_fee: lvl.test_reg_form_fee ?? 0,
      admission_fee: lvl.admission_fee ?? 0,
      development_fee: lvl.development_fee ?? 0,
      books_activity_fee: lvl.books_activity_fee ?? 0,
      tuition_fee: lvl.tuition_fee ?? 0,
      discount_amount: inv.discount_amount ?? autoDiscount,
      discount_label:
        inv.discount_label ||
        (app.promo_code_used ? `Kode Promo: ${app.promo_code_used}` : ''),
    });

    // Populate Payment Verify Form
    setPaymentVerifyForm({
      decision: 'verify',
      student_id_number: app.student_id_number || `PHS-${new Date().getFullYear()}-${String(app.id).padStart(4, '0')}`,
      official_receipt_doc_url: app.official_receipt_doc_url || '',
      admin_note: '',
    });
  };

  const handleValidateDocsSubmit = async (e) => {
    e.preventDefault();
    if (!selectedApp) return;
    setSubmittingDocs(true);
    setFeedback(null);
    try {
      const res = await fetch(`${apiBase}/admin/admissions/applications/${selectedApp.id}/validate-documents`, {
        method: 'POST',
        credentials: 'include',
        headers: getAuthHeaders(),
        body: JSON.stringify(docDecisionForm),
      });
      const data = await res.json();
      if (res.ok && data.ok) {
        setFeedback({ type: 'success', text: data.message });
        await loadAll();
      } else {
        setFeedback({ type: 'error', text: data.message || 'Gagal menyimpan validasi berkas' });
      }
    } catch (_) {
      setFeedback({ type: 'error', text: 'Kesalahan jaringan saat memvalidasi berkas' });
    } finally {
      setSubmittingDocs(false);
    }
  };

  const handleTestDocUpload = async (file) => {
    if (!file) return;

    const allowedExts = ['pdf', 'jpg', 'jpeg', 'png', 'webp'];
    const ext = (file.name || '').split('.').pop().toLowerCase();
    const forbiddenExts = ['xls', 'xlsx', 'csv', 'ods', 'doc', 'docx', 'txt', 'zip', 'rar'];

    if (forbiddenExts.includes(ext) || !allowedExts.includes(ext)) {
      setFeedback({
        type: 'error',
        text: `Format berkas .${ext.toUpperCase() || 'tidak valid'} tidak diizinkan. Harap unggah hasil scan dalam format PDF (.pdf) atau gambar (.jpg, .jpeg, .png, .webp). File spreadsheet/Excel tidak diperbolehkan.`,
      });
      return;
    }

    if (file.size > 15 * 1024 * 1024) {
      setFeedback({
        type: 'error',
        text: 'Ukuran file terlalu besar. Maksimal 15 MB.',
      });
      return;
    }

    setUploadingTestDoc(true);
    setFeedback(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      let res = await fetch(`${apiBase}/admissions/upload-file`, {
        method: 'POST',
        credentials: 'include',
        headers: getAuthHeaders(false),
        body: formData,
      });
      let data = await res.json().catch(() => null);

      // Fallback base64 upload if multipart was not accepted
      if (!res.ok || !data?.ok) {
        const base64 = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });
        res = await fetch(`${apiBase}/admissions/upload-file`, {
          method: 'POST',
          credentials: 'include',
          headers: getAuthHeaders(true),
          body: JSON.stringify({
            file_data: base64,
            file_name: file.name,
          }),
        });
        data = await res.json().catch(() => null);
      }

      if (res.ok && data?.ok && data.url) {
        setTestResultForm((prev) => ({ ...prev, test_result_doc_url: data.url }));
        setFeedback({
          type: 'success',
          text: 'Dokumen scan hasil tes (.pdf/gambar) berhasil diunggah dan terlampir pada berkas calon siswa.',
        });
      } else {
        setFeedback({
          type: 'error',
          text: data?.message || 'Gagal mengunggah dokumen hasil tes. Format harus PDF/JPG/PNG (maks 15MB).',
        });
      }
    } catch (_) {
      setFeedback({ type: 'error', text: 'Kesalahan jaringan saat mengunggah dokumen hasil tes' });
    } finally {
      setUploadingTestDoc(false);
      const modalBox = document.querySelector('.adm-modal-box');
      if (modalBox) modalBox.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleReceiptDocUpload = async (file) => {
    if (!file) return;

    const allowedExts = ['pdf', 'jpg', 'jpeg', 'png', 'webp'];
    const ext = (file.name || '').split('.').pop().toLowerCase();
    const forbiddenExts = ['xls', 'xlsx', 'csv', 'ods', 'doc', 'docx', 'txt', 'zip', 'rar'];

    if (forbiddenExts.includes(ext) || !allowedExts.includes(ext)) {
      setFeedback({
        type: 'error',
        text: `Format berkas .${ext.toUpperCase() || 'tidak valid'} tidak diizinkan. Harap unggah dalam format PDF (.pdf) atau gambar (.jpg, .jpeg, .png, .webp). File spreadsheet/Excel tidak diperbolehkan.`,
      });
      return;
    }

    if (file.size > 15 * 1024 * 1024) {
      setFeedback({
        type: 'error',
        text: 'Ukuran file terlalu besar. Maksimal 15 MB.',
      });
      return;
    }

    setUploadingReceiptDoc(true);
    setFeedback(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      let res = await fetch(`${apiBase}/admissions/upload-file`, {
        method: 'POST',
        credentials: 'include',
        headers: getAuthHeaders(false),
        body: formData,
      });
      let data = await res.json().catch(() => null);

      // Fallback base64 upload if multipart was not accepted
      if (!res.ok || !data?.ok) {
        const base64 = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });
        res = await fetch(`${apiBase}/admissions/upload-file`, {
          method: 'POST',
          credentials: 'include',
          headers: getAuthHeaders(true),
          body: JSON.stringify({
            file_data: base64,
            file_name: file.name,
          }),
        });
        data = await res.json().catch(() => null);
      }

      if (res.ok && data?.ok && data.url) {
        setPaymentVerifyForm((prev) => ({ ...prev, official_receipt_doc_url: data.url }));
        setFeedback({
          type: 'success',
          text: 'Berkas scan kwitansi fisik berhasil diunggah. Dokumen ini dapat diunduh oleh orang tua sebagai lampiran resmi.',
        });
      } else {
        setFeedback({
          type: 'error',
          text: data?.message || 'Gagal mengunggah berkas scan kwitansi fisik.',
        });
      }
    } catch (_) {
      setFeedback({ type: 'error', text: 'Kesalahan jaringan saat mengunggah berkas scan kwitansi fisik' });
    } finally {
      setUploadingReceiptDoc(false);
      const modalBox = document.querySelector('.adm-modal-box');
      if (modalBox) modalBox.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleTestResultSubmit = async (e) => {
    e.preventDefault();
    if (!selectedApp) return;

    if (testResultForm.status === 'passed' && !testResultForm.test_result_doc_url) {
      setFeedback({
        type: 'error',
        text: 'Wajib mengunggah lampiran dokumen hasil scan tes (.pdf / gambar) sebelum mengumumkan kelulusan calon siswa.',
      });
      const modalBox = document.querySelector('.adm-modal-box');
      if (modalBox) modalBox.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    setSubmittingTest(true);
    setFeedback(null);
    try {
      const res = await fetch(`${apiBase}/admin/admissions/applications/${selectedApp.id}/test-result`, {
        method: 'POST',
        credentials: 'include',
        headers: getAuthHeaders(),
        body: JSON.stringify(testResultForm),
      });
      const data = await res.json();
      if (res.ok && data.ok) {
        setFeedback({ type: 'success', text: data.message });
        await loadAll();
      } else {
        setFeedback({ type: 'error', text: data.message || 'Gagal menyimpan hasil tes' });
      }
    } catch (_) {
      setFeedback({ type: 'error', text: 'Kesalahan jaringan saat menyimpan hasil tes' });
    } finally {
      setSubmittingTest(false);
      const modalBox = document.querySelector('.adm-modal-box');
      if (modalBox) modalBox.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleVerifyPaymentSubmit = async (e) => {
    e.preventDefault();
    if (!selectedApp) return;
    setSubmittingVerify(true);
    setFeedback(null);
    try {
      const res = await fetch(`${apiBase}/admin/admissions/applications/${selectedApp.id}/verify-payment`, {
        method: 'POST',
        credentials: 'include',
        headers: getAuthHeaders(),
        body: JSON.stringify(paymentVerifyForm),
      });
      const data = await res.json();
      if (res.ok && data.ok) {
        setFeedback({ type: 'success', text: data.message });
        await loadAll();
      } else {
        setFeedback({ type: 'error', text: data.message || 'Gagal memverifikasi pembayaran' });
      }
    } catch (_) {
      setFeedback({ type: 'error', text: 'Kesalahan jaringan saat memverifikasi pembayaran' });
    } finally {
      setSubmittingVerify(false);
    }
  };

  const handleSavePeriod = async (e) => {
    e.preventDefault();
    if (isSavingPeriod) return;
    setIsSavingPeriod(true);
    setFeedback(null);
    try {
      const url = editingPeriod
        ? `${apiBase}/admin/admissions/periods/${editingPeriod.id}`
        : `${apiBase}/admin/admissions/periods`;
      const method = editingPeriod ? 'PUT' : 'POST';

      const res = await fetch(url, {
        method,
        credentials: 'include',
        headers: getAuthHeaders(),
        body: JSON.stringify(periodForm),
      });
      const data = await res.json();
      if (res.ok && data.ok) {
        setFeedback({ type: 'success', text: data.message });
        setEditingPeriod(null);
        setIsPeriodModalOpen(false);
        setPeriodForm({
          period_name: '',
          academic_year: '2026/2027',
          start_date: '',
          end_date: '',
          quotas: { preschool: 25, kindergarten: 30, primary: 60, secondary: 45, sma: 0 },
          is_active: true,
          next_period_id: '',
          notes: '',
        });
        await loadAll();
      } else {
        setFeedback({ type: 'error', text: data.message || 'Gagal menyimpan periode' });
      }
    } catch (_) {
      setFeedback({ type: 'error', text: 'Gagal menyimpan periode pendaftaran' });
    } finally {
      setIsSavingPeriod(false);
    }
  };

  const handleDeletePeriod = async (pid) => {
    if (!window.confirm('Apakah Anda yakin ingin menghapus periode pendaftaran ini?')) return;
    try {
      const res = await fetch(`${apiBase}/admin/admissions/periods/${pid}`, {
        method: 'DELETE',
        credentials: 'include',
        headers: getAuthHeaders(),
      });
      const data = await res.json();
      setFeedback({ type: res.ok ? 'success' : 'error', text: data.message });
      await loadAll();
    } catch (_) {
      setFeedback({ type: 'error', text: 'Gagal menghapus periode pendaftaran' });
    }
  };

  const handleCreateSlot = async (e) => {
    e.preventDefault();
    if (isSavingSlot) return;
    setIsSavingSlot(true);
    setFeedback(null);
    try {
      const url = editingSlot
        ? `${apiBase}/admin/admissions/test-slots/${editingSlot.id}`
        : `${apiBase}/admin/admissions/test-slots`;
      const method = editingSlot ? 'PUT' : 'POST';

      const res = await fetch(url, {
        method,
        credentials: 'include',
        headers: getAuthHeaders(),
        body: JSON.stringify(slotForm),
      });
      const data = await res.json();
      if (res.ok && data.ok) {
        setFeedback({ type: 'success', text: data.message });
        setIsSlotModalOpen(false);
        setEditingSlot(null);
        setSlotForm({
          period_id: overview.periods?.[0]?.id || 1,
          level_code: 'all',
          session_name: '',
          test_date: '',
          test_time: '09:00 - 11:00 WIB',
          campus_location: 'Campus 1 (Pos Pengumben) - Jl. Pos Pengumben Lama No.13',
          room_name: 'Gedung Utama Lt. 2 - Ruang Assessment A',
          slot_quota: 15,
          custom_notes: '',
        });
        await loadAll();
      } else {
        setFeedback({ type: 'error', text: data.message || 'Gagal menambahkan jadwal tes' });
      }
    } catch (_) {
      setFeedback({ type: 'error', text: 'Gagal menambahkan sesi jadwal tes' });
    } finally {
      setIsSavingSlot(false);
    }
  };

  const handleDeleteSlot = async (slotId) => {
    if (!window.confirm('Apakah Anda yakin ingin menghapus sesi jadwal tes ini?')) return;
    try {
      const res = await fetch(`${apiBase}/admin/admissions/test-slots/${slotId}`, {
        method: 'DELETE',
        credentials: 'include',
        headers: getAuthHeaders(),
      });
      const data = await res.json();
      setFeedback({ type: res.ok ? 'success' : 'error', text: data.message });
      await loadAll();
    } catch (_) {
      setFeedback({ type: 'error', text: 'Gagal menghapus sesi jadwal tes' });
    }
  };

  const handleSaveLevelConfig = async (e) => {
    e.preventDefault();
    if (!selectedLevelCfg) return;
    setSavingLevelCfg(true);
    setFeedback(null);
    try {
      const res = await fetch(`${apiBase}/admin/admissions/levels/${selectedLevelCfg.level_code}`, {
        method: 'PUT',
        credentials: 'include',
        headers: getAuthHeaders(),
        body: JSON.stringify(selectedLevelCfg),
      });
      const data = await res.json();
      if (res.ok && data.ok) {
        setFeedback({ type: 'success', text: data.message });
        setIsFeeModalOpen(false);
        await loadAll();
      } else {
        setFeedback({ type: 'error', text: data.message || 'Gagal menyimpan konfigurasi jenjang' });
      }
    } catch (_) {
      setFeedback({ type: 'error', text: 'Gagal menyimpan konfigurasi jenjang' });
    } finally {
      setSavingLevelCfg(false);
    }
  };

  const handleCreatePromo = async (e) => {
    e.preventDefault();
    if (isSavingPromo) return;
    setIsSavingPromo(true);
    setFeedback(null);
    try {
      const res = await fetch(`${apiBase}/admin/admissions/promo-codes`, {
        method: 'POST',
        credentials: 'include',
        headers: getAuthHeaders(),
        body: JSON.stringify(promoForm),
      });
      const data = await res.json();
      if (res.ok && data.ok) {
        setFeedback({ type: 'success', text: data.message });
        setIsPromoModalOpen(false);
        setPromoForm({
          promo_code: '',
          promo_title: '',
          discount_type: 'fixed',
          discount_value: '',
          applies_to_levels: 'all',
          max_quota: 100,
          valid_until: '2027-06-30',
        });
        await loadAll();
      } else {
        setFeedback({ type: 'error', text: data.message || 'Gagal membuat kode promo' });
      }
    } catch (_) {
      setFeedback({ type: 'error', text: 'Gagal menyimpan kode promo' });
    } finally {
      setIsSavingPromo(false);
    }
  };

  const handleDeletePromo = async (id) => {
    if (!window.confirm('Apakah Anda yakin ingin menghapus kode promo ini?')) return;
    try {
      const res = await fetch(`${apiBase}/admin/admissions/promo-codes/${id}`, {
        method: 'DELETE',
        credentials: 'include',
        headers: getAuthHeaders(),
      });
      const data = await res.json();
      setFeedback({ type: res.ok ? 'success' : 'error', text: data.message });
      await loadAll();
    } catch (_) {
      setFeedback({ type: 'error', text: 'Gagal menghapus kode promo' });
    }
  };

  const handleViewPromoUsers = async (promo) => {
    setLoadingPromoUsers(true);
    setPromoHistoryModal({ promo, users: [] });
    try {
      const res = await fetch(`${apiBase}/admin/admissions/promo-codes/${promo.id}/users`, {
        credentials: 'include',
        headers: getAuthHeaders(),
      });
      const data = await res.json();
      if (res.ok && data.ok) {
        setPromoHistoryModal({ promo: data.data?.promo || promo, users: data.data?.users || [] });
      } else {
        setFeedback({ type: 'error', text: data.message || 'Gagal memuat daftar pengguna promo' });
      }
    } catch (_) {
      setFeedback({ type: 'error', text: 'Gagal memuat daftar pengguna promo' });
    } finally {
      setLoadingPromoUsers(false);
    }
  };

  const handleOpenDetailPromo = async (promo) => {
    setDetailPromoModal(promo);
    setDetailPromoUsers([]);
    setLoadingDetailPromoUsers(true);
    try {
      const res = await fetch(`${apiBase}/admin/admissions/promo-codes/${promo.id}/users`, {
        credentials: 'include',
        headers: getAuthHeaders(),
      });
      const data = await res.json();
      if (res.ok && data.ok) {
        setDetailPromoUsers(data.users || data.data?.users || []);
        if (data.promo || data.data?.promo) {
          setDetailPromoModal((prev) => ({ ...prev, ...(data.promo || data.data?.promo) }));
        }
      } else {
        setFeedback({ type: 'error', text: data.message || 'Gagal memuat rincian voucher promo' });
      }
    } catch (_) {
      setFeedback({ type: 'error', text: 'Gagal memuat rincian voucher promo' });
    } finally {
      setLoadingDetailPromoUsers(false);
    }
  };

  const resolveFileLink = (url) => {
    if (!url) return '';
    if (url.startsWith('http://') || url.startsWith('https://')) return url;
    const baseRoot = apiBase.replace(/\/api\/?$/, '');
    return `${baseRoot}${url.startsWith('/') ? '' : '/'}${url}`;
  };

  const getStatusBadge = (status) => {
    const map = {
      submitted: { label: 'Berkas Masuk (Baru)', cls: 'adm-badge-blue' },
      document_revision: { label: 'Revisi Berkas', cls: 'adm-badge-orange' },
      test_scheduled: { label: 'Jadwal Tes Terbit', cls: 'adm-badge-purple' },
      test_passed: { label: 'Lulus - Tunggu Bayar', cls: 'adm-badge-teal' },
      test_failed: { label: 'Tidak Lulus', cls: 'adm-badge-red' },
      payment_submitted: { label: 'Verifikasi Bukti Bayar', cls: 'adm-badge-yellow' },
      payment_revision: { label: 'Revisi Bukti Bayar', cls: 'adm-badge-orange' },
      enrolled: { label: 'Resmi Siswa (Enrolled)', cls: 'adm-badge-green' },
    };
    const st = map[status] || { label: status, cls: 'adm-badge-gray' };
    return <span className={`adm-status-pill ${st.cls}`}>{st.label}</span>;
  };

  const academicYearOptions = Array.from(
    new Set([
      '2026/2027',
      ...(overview.periods || []).map((p) => p.academic_year).filter(Boolean),
      ...applications.map((a) => a.academic_year).filter(Boolean),
    ])
  ).sort().reverse();

  const relevantPeriods = (overview.periods || []).filter((p) => {
    if (filterAcademicYear && filterAcademicYear !== 'all') {
      return p.academic_year === filterAcademicYear;
    }
    return true;
  });

  const periodFilteredApps = applications.filter((a) => {
    if (filterAcademicYear && filterAcademicYear !== 'all' && a.academic_year !== filterAcademicYear) return false;
    if (filterPeriod && String(a.period_id) !== String(filterPeriod)) return false;
    return true;
  });

  const levelCounts = periodFilteredApps.reduce((acc, a) => {
    const k = a.level_code || 'other';
    acc[k] = (acc[k] || 0) + 1;
    return acc;
  }, {});

  const computedQuotas = { preschool: 0, kindergarten: 0, primary: 0, secondary: 0 };
  const targetPeriods = filterPeriod
    ? (overview.periods || []).filter((p) => String(p.id) === String(filterPeriod))
    : relevantPeriods;

  targetPeriods.forEach((p) => {
    const q = p.quotas || {};
    Object.keys(computedQuotas).forEach((lvlKey) => {
      computedQuotas[lvlKey] += Number(q[lvlKey] || 0);
    });
  });

  // Deduplicated config lists to defensively prevent duplicate rows in UI
  const displayPeriods = (overview.periods || []).filter((p, idx, self) =>
    idx === self.findIndex((item) => (item.period_name || '').toLowerCase().trim() === (p.period_name || '').toLowerCase().trim())
  );

  const displaySlots = (overview.test_slots || []).filter((sl, idx, self) => {
    const key = `${(sl.session_name || '').toLowerCase().trim()}_${sl.test_date || ''}`;
    return idx === self.findIndex((item) => {
      const k = `${(item.session_name || '').toLowerCase().trim()}_${item.test_date || ''}`;
      return k === key;
    });
  });

  const displayPromos = (overview.promo_codes || []).filter((pr, idx, self) =>
    idx === self.findIndex((item) => (item.promo_code || '').toUpperCase().trim() === (pr.promo_code || '').toUpperCase().trim())
  );

  const filteredApplications = applications.filter((a) => {
    if (filterAcademicYear && filterAcademicYear !== 'all' && a.academic_year !== filterAcademicYear) return false;
    if (filterPeriod && String(a.period_id) !== String(filterPeriod)) return false;
    if (filterLevel && a.level_code !== filterLevel) return false;
    if (filterStatus && a.status_code !== filterStatus) return false;
    if (filterCampus && !String(a.campus_location || '').toLowerCase().includes(filterCampus.toLowerCase())) {
      return false;
    }
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      const hay = `${a.child_full_name} ${a.registration_no} ${a.applicant_name} ${a.applicant_email} ${a.student_id_number || ''}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });

  const exportToExcel = () => {
    const dataToExport = filteredApplications;
    if (!dataToExport || dataToExport.length === 0) {
      setFeedback({ type: 'warning', text: 'Tidak ada data pendaftar yang dapat diekspor dengan filter saat ini.' });
      return;
    }

    const exportDate = new Date().toLocaleString('id-ID');
    const yearLabel = filterAcademicYear === 'all' ? 'Semua Tahun Ajaran' : filterAcademicYear || '2026/2027';
    const periodLabel = filterPeriod
      ? overview.periods.find((p) => String(p.id) === String(filterPeriod))?.period_name || `Periode #${filterPeriod}`
      : 'Semua Gelombang';

    const STATUS_MAP = {
      submitted: 'Berkas Masuk (Baru)',
      document_revision: 'Revisi Berkas',
      test_scheduled: 'Jadwal Tes Terbit',
      test_passed: 'Lulus Seleksi (Tunggu Bayar)',
      test_failed: 'Tidak Lulus',
      payment_submitted: 'Verifikasi Bukti Bayar',
      payment_revision: 'Revisi Bukti Bayar',
      enrolled: 'Resmi Siswa (Enrolled)',
    };

    const rowsXml = dataToExport
      .map((app, idx) => {
        const statusText = STATUS_MAP[app.status_code] || app.status_code;
        const testScore = app.test_score !== null && app.test_score !== undefined ? app.test_score : '-';
        const amountPaid = app.payment_amount_paid || (app.invoice ? app.invoice.total_payable : 0) || 0;
        const phone = app.applicant_phone || app.parent_info?.phone || '-';
        const cleanPhone = phone.replace(/[^\d+]/g, '');

        return `
      <Row>
        <Cell ss:StyleID="Default"><Data ss:Type="Number">${idx + 1}</Data></Cell>
        <Cell ss:StyleID="TextCell"><Data ss:Type="String">${escapeXml(app.registration_no || '')}</Data></Cell>
        <Cell ss:StyleID="TextCell"><Data ss:Type="String">${escapeXml(app.student_id_number || '-')}</Data></Cell>
        <Cell ss:StyleID="Default"><Data ss:Type="String">${escapeXml(app.child_full_name || '')}</Data></Cell>
        <Cell ss:StyleID="Default"><Data ss:Type="String">${escapeXml(app.level_name || '')} (${escapeXml(app.grade_target || '-')})</Data></Cell>
        <Cell ss:StyleID="Default"><Data ss:Type="String">${escapeXml(app.academic_year || '-')}</Data></Cell>
        <Cell ss:StyleID="Default"><Data ss:Type="String">${escapeXml(app.period_name || '-')}</Data></Cell>
        <Cell ss:StyleID="Default"><Data ss:Type="String">${escapeXml(app.campus_location || '-')}</Data></Cell>
        <Cell ss:StyleID="Default"><Data ss:Type="String">${escapeXml(app.applicant_name || '')}</Data></Cell>
        <Cell ss:StyleID="TextCell"><Data ss:Type="String">${escapeXml(cleanPhone)}</Data></Cell>
        <Cell ss:StyleID="Default"><Data ss:Type="String">${escapeXml(app.applicant_email || '')}</Data></Cell>
        <Cell ss:StyleID="Default"><Data ss:Type="String">${escapeXml(statusText)}</Data></Cell>
        <Cell ss:StyleID="Default"><Data ss:Type="String">${escapeXml(String(testScore))}</Data></Cell>
        <Cell ss:StyleID="CurrencyCell"><Data ss:Type="Number">${amountPaid}</Data></Cell>
        <Cell ss:StyleID="Default"><Data ss:Type="String">${escapeXml(app.payment_method || '-')}</Data></Cell>
      </Row>`;
      })
      .join('');

    const xmlContent = `<?xml version="1.0" encoding="UTF-8"?>
<?mso-application progid="Excel.Sheet"?>
<Workbook xmlns="urn:schemas-microsoft-com:office:spreadsheet"
 xmlns:o="urn:schemas-microsoft-com:office:office"
 xmlns:x="urn:schemas-microsoft-com:office:excel"
 xmlns:ss="urn:schemas-microsoft-com:office:spreadsheet"
 xmlns:html="http://www.w3.org/TR/REC-html40">
 <Styles>
  <Style ss:ID="Default" ss:Name="Normal">
   <Alignment ss:Vertical="Center"/>
   <Font ss:FontName="Calibri" ss:Size="11" ss:Color="#000000"/>
  </Style>
  <Style ss:ID="Header">
   <Alignment ss:Horizontal="Center" ss:Vertical="Center"/>
   <Borders>
    <Border ss:Position="Bottom" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#1e3a8a"/>
   </Borders>
   <Font ss:FontName="Calibri" ss:Size="11" ss:Color="#FFFFFF" ss:Bold="1"/>
   <Interior ss:Color="#1e3a8a" ss:Pattern="Solid"/>
  </Style>
  <Style ss:ID="Title">
   <Font ss:FontName="Calibri" ss:Size="14" ss:Color="#1e3a8a" ss:Bold="1"/>
  </Style>
  <Style ss:ID="SubTitle">
   <Font ss:FontName="Calibri" ss:Size="10" ss:Color="#4b5563" ss:Italic="1"/>
  </Style>
  <Style ss:ID="TextCell">
   <NumberFormat ss:Format="@"/>
   <Alignment ss:Vertical="Center"/>
  </Style>
  <Style ss:ID="CurrencyCell">
   <NumberFormat ss:Format="Rp #,##0"/>
   <Alignment ss:Horizontal="Right" ss:Vertical="Center"/>
  </Style>
 </Styles>
 <Worksheet ss:Name="Data Pendaftar">
  <Table>
   <Column ss:Width="40"/>
   <Column ss:Width="130"/>
   <Column ss:Width="110"/>
   <Column ss:Width="160"/>
   <Column ss:Width="120"/>
   <Column ss:Width="90"/>
   <Column ss:Width="130"/>
   <Column ss:Width="140"/>
   <Column ss:Width="150"/>
   <Column ss:Width="110"/>
   <Column ss:Width="160"/>
   <Column ss:Width="140"/>
   <Column ss:Width="80"/>
   <Column ss:Width="120"/>
   <Column ss:Width="120"/>
   <Row>
    <Cell ss:MergeAcross="14" ss:StyleID="Title"><Data ss:Type="String">LAPORAN DATA PENDAFTAR PPDB - SEKOLAH PERMATA HARAPAN</Data></Cell>
   </Row>
   <Row>
    <Cell ss:MergeAcross="14" ss:StyleID="SubTitle"><Data ss:Type="String">Tahun Ajaran: ${escapeXml(yearLabel)} | Gelombang: ${escapeXml(periodLabel)} | Tanggal Ekspor: ${escapeXml(exportDate)} | Total: ${dataToExport.length} Pendaftar</Data></Cell>
   </Row>
   <Row ss:Index="4">
    <Cell ss:StyleID="Header"><Data ss:Type="String">No</Data></Cell>
    <Cell ss:StyleID="Header"><Data ss:Type="String">No. Registrasi</Data></Cell>
    <Cell ss:StyleID="Header"><Data ss:Type="String">NIS Siswa</Data></Cell>
    <Cell ss:StyleID="Header"><Data ss:Type="String">Nama Calon Siswa</Data></Cell>
    <Cell ss:StyleID="Header"><Data ss:Type="String">Jenjang &amp; Kelas</Data></Cell>
    <Cell ss:StyleID="Header"><Data ss:Type="String">Tahun Ajaran</Data></Cell>
    <Cell ss:StyleID="Header"><Data ss:Type="String">Gelombang / Periode</Data></Cell>
    <Cell ss:StyleID="Header"><Data ss:Type="String">Lokasi Kampus</Data></Cell>
    <Cell ss:StyleID="Header"><Data ss:Type="String">Nama Orang Tua</Data></Cell>
    <Cell ss:StyleID="Header"><Data ss:Type="String">No. Telepon / WA</Data></Cell>
    <Cell ss:StyleID="Header"><Data ss:Type="String">Email Orang Tua</Data></Cell>
    <Cell ss:StyleID="Header"><Data ss:Type="String">Status Tahapan</Data></Cell>
    <Cell ss:StyleID="Header"><Data ss:Type="String">Nilai Tes</Data></Cell>
    <Cell ss:StyleID="Header"><Data ss:Type="String">Nominal Tagihan / Bayar</Data></Cell>
    <Cell ss:StyleID="Header"><Data ss:Type="String">Metode Bayar</Data></Cell>
   </Row>
   ${rowsXml}
  </Table>
 </Worksheet>
</Workbook>`;

    const blob = new Blob([xmlContent], { type: 'application/vnd.ms-excel;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const fileNameYear = filterAcademicYear && filterAcademicYear !== 'all' ? filterAcademicYear.replace('/', '-') : 'Semua';
    a.download = `Data_Pendaftar_PermataHarapan_${fileNameYear}_${new Date().toISOString().slice(0, 10)}.xls`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const pendingActionCount = applications.filter((a) =>
    ['submitted', 'payment_submitted'].includes(a.status_code)
  ).length;

  const handleTabChange = (tabId) => {
    setFeedback(null);
    if (tabId === 'applicants') {
      setFilterLevel('');
    } else if (tabId.startsWith('applicants-')) {
      setFilterLevel(tabId.replace('applicants-', ''));
    }
    setActiveTab(tabId);
  };

  useEffect(() => {
    setFeedback(null);
  }, [activeTab]);

  const CmsFormComponent = activeTab.startsWith('cms-adm-') ? formComponentMap[activeTab] : null;

  return (
    <DashboardLayout
      user={user}
      onLogout={onLogout}
      onTabChange={handleTabChange}
      userRole="admissions"
      apiBase={apiBase}
      activeTab={activeTab}
      applicantsCount={pendingActionCount}
      levelCounts={levelCounts}
      totalApplicants={applications.length}
    >
      {CmsFormComponent ? (
        <CmsFormComponent apiBase={apiBase} activeTab={activeTab} />
      ) : (
      <div className="adm-portal-container">

        {/* ===================================================================
            TAB 1: DASHBOARD OVERVIEW
        =================================================================== */}
        {activeTab === 'dashboard' && (
          <div className="adm-tab-panel">
            <div className="adm-panel-head">
              <div>
                <h2>Ringkasan Penerimaan Siswa Baru (PPDB)</h2>
                <p>Monitoring alur pendaftaran multi-anak, kuota periode, jadwal tes offline, hingga pengesahan siswa.</p>
              </div>
              <div>
                <button type="button" className="adm-btn-outline" onClick={loadAll}>
                  <RefreshCw size={15} />
                  <span>Refresh Data</span>
                </button>
              </div>
            </div>

            {/* Admissions Operational Advisory (Alerts if no active periods exist) */}
            {!loading && (
              (overview.readiness && !overview.readiness.is_ready) ||
              !overview.periods ||
              overview.periods.length === 0 ||
              !overview.periods.some((p) => p.is_active)
            ) && (
              <div
                style={{
                  background: '#fffbeb',
                  border: '1.5px solid #fde68a',
                  borderRadius: 12,
                  padding: '14px 18px',
                  marginBottom: 20,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 14
                }}
              >
                <AlertTriangle size={22} color="#d97706" style={{ flexShrink: 0 }} />
                <div style={{ flex: 1 }}>
                  <strong style={{ fontSize: 14, color: '#92400e' }}>
                    Pengingat Admin Admissions: Belum Ada Periode Pendaftaran Aktif
                  </strong>
                  <p style={{ margin: '3px 0 0', fontSize: 13, color: '#78350f', lineHeight: 1.45 }}>
                    Sistem belum mendeteksi gelombang pendaftaran yang aktif. Silakan tambahkan gelombang pada tab <strong>Periode &amp; Kuota</strong> agar pendaftaran baru otomatis teralokasi ke periode tersebut.
                  </p>
                </div>
                <button
                  type="button"
                  className="adm-btn-primary"
                  style={{ padding: '8px 16px', fontSize: 12.5, whiteSpace: 'nowrap' }}
                  onClick={() => setActiveTab('periods')}
                >
                  Atur Periode
                </button>
              </div>
            )}

            <div className="adm-kpi-grid">
              <div className="adm-kpi-card" onClick={() => { setFilterStatus(''); setActiveTab('applicants'); }}>
                <span className="adm-kpi-label">Total Anak Terdaftar</span>
                <strong className="adm-kpi-val">{overview.total_applicants}</strong>
                <span className="adm-kpi-sub">Seluruh jenjang & kampus</span>
              </div>

              <div className="adm-kpi-card" onClick={() => { setFilterStatus('submitted'); setActiveTab('applicants'); }}>
                <span className="adm-kpi-label">Menunggu Validasi Berkas</span>
                <strong className="adm-kpi-val blue">{overview.by_status?.submitted || 0}</strong>
                <span className="adm-kpi-sub">Perlu diperiksa & dijadwalkan tes</span>
              </div>

              <div className="adm-kpi-card" onClick={() => { setFilterStatus('test_scheduled'); setActiveTab('applicants'); }}>
                <span className="adm-kpi-label">Terjadwal Tes Offline</span>
                <strong className="adm-kpi-val purple">{overview.by_status?.test_scheduled || 0}</strong>
                <span className="adm-kpi-sub">Menunggu pelaksanaan & input nilai</span>
              </div>

              <div className="adm-kpi-card" onClick={() => { setFilterStatus('payment_submitted'); setActiveTab('applicants'); }}>
                <span className="adm-kpi-label">Verifikasi Bukti Bayar</span>
                <strong className="adm-kpi-val orange">{overview.by_status?.payment_submitted || 0}</strong>
                <span className="adm-kpi-sub">Bukti bayar diunggah orang tua</span>
              </div>

              <div className="adm-kpi-card" onClick={() => { setFilterStatus('enrolled'); setActiveTab('applicants'); }}>
                <span className="adm-kpi-label">Resmi Dinyatakan Siswa</span>
                <strong className="adm-kpi-val green">{overview.by_status?.enrolled || 0}</strong>
                <span className="adm-kpi-sub">Pembayaran lunas & NIS terbit</span>
              </div>
            </div>

            {/* Quota Progress per Period & Upcoming Test Slots */}
            <div className="adm-two-col">
              <div className="adm-card">
                <div className="adm-card-head">
                  <h3>Kuota Periode Pendaftaran & Auto-Rollover</h3>
                  <button type="button" className="adm-link-btn" onClick={() => setActiveTab('periods')}>
                    Kelola Periode
                  </button>
                </div>
                {(overview.periods || []).map((p) => (
                  <div key={p.id} className="adm-period-summary">
                    <div className="adm-period-title-row">
                      <strong>{p.period_name}</strong>
                      <span className="adm-date-chip">
                        {formatDateId(p.start_date)} - {formatDateId(p.end_date)}
                      </span>
                    </div>
                    <div className="adm-quota-bars">
                      {['preschool', 'kindergarten', 'primary', 'secondary'].map((lc) => {
                        const maxQ = Number(p.quotas?.[lc] ?? 30);
                        const usedQ = Number(p.filled?.[lc] ?? 0);
                        const pct = maxQ > 0 ? Math.min(100, Math.round((usedQ / maxQ) * 100)) : 0;
                        return (
                          <div key={lc} className="adm-quota-row">
                            <div className="adm-quota-row-top">
                              <span>{lc.toUpperCase()}</span>
                              <span>
                                {usedQ} / {maxQ} Kursi ({pct}%)
                              </span>
                            </div>
                            <div className="adm-progress-track">
                              <div
                                className={`adm-progress-fill ${pct >= 100 ? 'full' : ''}`}
                                style={{ width: `${pct}%` }}
                              />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>

              <div className="adm-card">
                <div className="adm-card-head">
                  <h3>Kapasitas Sesi Jadwal Tes Offline</h3>
                  <button type="button" className="adm-link-btn" onClick={() => setActiveTab('test-slots')}>
                    Tambah Jadwal Tes
                  </button>
                </div>
                <div className="adm-slot-list">
                  {(overview.test_slots || []).map((sl) => {
                    const isFull = sl.booked_count >= sl.slot_quota;
                    return (
                      <div key={sl.id} className="adm-slot-item">
                        <div>
                          <strong>{sl.session_name}</strong>
                          <p>
                            {formatDateId(sl.test_date)} | {sl.test_time}
                          </p>
                          <span className="adm-slot-loc">
                            {sl.campus_location} ({sl.room_name})
                          </span>
                        </div>
                        <div className="adm-slot-cap">
                          <span className={`adm-cap-badge ${isFull ? 'full' : 'avail'}`}>
                            {sl.booked_count} / {sl.slot_quota} Peserta
                          </span>
                          {isFull && <small>Penuh (Auto ke Sesi Berikutnya)</small>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ===================================================================
            TAB 2: APPLICANTS, DOCUMENT VALIDATION, TEST RESULTS & PAYMENT VERIFY
        =================================================================== */}
        {activeTab.startsWith('applicants') && (
          <div className="adm-tab-panel">
            <div className="adm-panel-head">
              <div>
                <h2>
                  {filterLevel
                    ? `Pendaftar ${LEVEL_TABS.find((l) => l.code === filterLevel)?.label || filterLevel}`
                    : 'Semua Pendaftar'}
                </h2>
                <p>Klik baris pendaftar untuk memvalidasi berkas, menerbitkan jadwal tes, menginput nilai & LoA, atau memverifikasi bukti bayar.</p>
              </div>
              <button type="button" className="adm-btn-outline" onClick={loadAll}>
                <RefreshCw size={15} />
                <span>Refresh</span>
              </button>
            </div>

            {/* Academic Year & Period Selector Bar + Excel Export */}
            <div className="adm-period-selector-bar" style={{ marginBottom: 14 }}>
              <div className="adm-period-selector-left">
                <div className="adm-period-select-field">
                  <label>
                    <Calendar size={14} color="#2563eb" />
                    <span>Tahun Ajaran:</span>
                  </label>
                  <select
                    value={filterAcademicYear}
                    onChange={(e) => {
                      setFilterAcademicYear(e.target.value);
                      setFilterPeriod('');
                    }}
                    className="adm-select-academic-year"
                  >
                    <option value="all">Semua Tahun Ajaran</option>
                    {academicYearOptions.map((yr) => (
                      <option key={yr} value={yr}>
                        T.A. {yr}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="adm-period-select-field">
                  <label>
                    <Clock size={14} color="#d97706" />
                    <span>Gelombang / Periode:</span>
                  </label>
                  <select
                    value={filterPeriod}
                    onChange={(e) => setFilterPeriod(e.target.value)}
                    className="adm-select-period"
                  >
                    <option value="">
                      Semua Gelombang ({filterAcademicYear === 'all' ? 'Semua T.A' : `T.A ${filterAcademicYear}`})
                    </option>
                    {relevantPeriods.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.period_name} ({p.academic_year}) {p.is_active ? '• Aktif' : ''}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="adm-period-selector-right">
                <button
                  type="button"
                  className="adm-btn-excel"
                  onClick={exportToExcel}
                  title="Unduh data pendaftar dalam format Microsoft Excel (.xls)"
                >
                  <FileSpreadsheet size={16} />
                  <span>Export Excel ({filteredApplications.length})</span>
                </button>
              </div>
            </div>

            {/* 4-Column Level Quota & Applicant Summary Bar */}
            <div className="adm-level-summary-grid" style={{ marginBottom: 16 }}>
              {[
                { code: 'preschool', name: 'Preschool', tag: 'KB', color: '#10b981' },
                { code: 'kindergarten', name: 'Kindergarten', tag: 'TK', color: '#06b6d4' },
                { code: 'primary', name: 'Primary School', tag: 'SD', color: '#3b82f6' },
                { code: 'secondary', name: 'Secondary School', tag: 'SMP', color: '#8b5cf6' },
              ].map((lvl) => {
                const filled = levelCounts[lvl.code] || 0;
                const quota = computedQuotas[lvl.code] || 0;
                const sisa = Math.max(0, quota - filled);
                const pct = quota > 0 ? Math.min(100, Math.round((filled / quota) * 100)) : 0;
                const isSelected = filterLevel === lvl.code;

                return (
                  <div
                    key={lvl.code}
                    className={`adm-level-summary-card ${isSelected ? 'active' : ''}`}
                    onClick={() => {
                      if (filterLevel === lvl.code) {
                        handleTabChange('applicants');
                      } else {
                        handleTabChange(`applicants-${lvl.code}`);
                      }
                    }}
                    title={`Klik untuk filter data ${lvl.name}`}
                  >
                    <div className="adm-lsc-top">
                      <span className="adm-lsc-name">{lvl.name}</span>
                      <span className="adm-lsc-tag" style={{ backgroundColor: lvl.color }}>
                        {lvl.tag}
                      </span>
                    </div>
                    <div className="adm-lsc-metrics">
                      <div>
                        <span className="adm-lsc-metric-big">{filled}</span>
                        <span className="adm-lsc-metric-sub"> / {quota > 0 ? quota : '-'} pendaftar</span>
                      </div>
                      <span className="adm-lsc-metric-sub" style={{ fontWeight: 600 }}>
                        {quota > 0 ? (sisa === 0 ? 'Kuota Penuh' : `Sisa ${sisa}`) : `${filled} Terisi`}
                      </span>
                    </div>
                    {quota > 0 && (
                      <div className="adm-quota-bar-track">
                        <div
                          className="adm-quota-bar-fill"
                          style={{
                            width: `${pct}%`,
                            backgroundColor: pct >= 100 ? '#ef4444' : lvl.color,
                          }}
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="adm-level-tabs">
              <button
                type="button"
                className={`adm-level-tab ${!filterLevel ? 'active' : ''}`}
                onClick={() => handleTabChange('applicants')}
              >
                <span>Semua</span>
                <span className="adm-level-tab-count">{periodFilteredApps.length}</span>
              </button>
              {LEVEL_TABS.filter((lv) => lv.code !== 'sma').map((lv) => (
                <button
                  key={lv.code}
                  type="button"
                  className={`adm-level-tab ${filterLevel === lv.code ? 'active' : ''}`}
                  onClick={() => handleTabChange(`applicants-${lv.code}`)}
                >
                  <span>{lv.label}</span>
                  <span className="adm-level-tab-count">{levelCounts[lv.code] || 0}</span>
                </button>
              ))}
            </div>

            <div className="adm-filter-bar">
              <div className="adm-search-box">
                <Search size={16} />
                <input
                  type="text"
                  placeholder="Cari nama anak, no. registrasi, nama orang tua, atau NIS..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>

              <select value={filterCampus} onChange={(e) => setFilterCampus(e.target.value)}>
                <option value="">Semua Kampus</option>
                <option value="Pos Pengumben">Campus 1 (Pos Pengumben)</option>
                <option value="Srengseng">Campus 2 (Srengseng)</option>
              </select>

              <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
                <option value="">Semua Status Tahapan</option>
                <option value="submitted">1. Menunggu Validasi Berkas</option>
                <option value="document_revision">2. Revisi Berkas</option>
                <option value="test_scheduled">3. Jadwal Tes Offline Terbit</option>
                <option value="test_passed">4. Lulus Seleksi (Tunggu Bayar)</option>
                <option value="test_failed">5. Tidak Lulus</option>
                <option value="payment_submitted">6. Verifikasi Bukti Bayar</option>
                <option value="payment_revision">7. Revisi Bukti Bayar</option>
                <option value="enrolled">8. Resmi Siswa (Enrolled)</option>
              </select>
            </div>

            <div className="adm-table-card">
              <table className="adm-table">
                <thead>
                  <tr>
                    <th>No. Reg / NIS</th>
                    <th>Nama Anak & Kelas</th>
                    <th>Orang Tua (Akun)</th>
                    <th>Kampus & Periode</th>
                    <th>Status Tahapan</th>
                    <th>Jadwal / Nilai Tes</th>
                    <th>Tagihan / Bayar</th>
                    <th>Aksi</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredApplications.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="adm-empty-cell">
                        Tidak ada data pendaftar yang sesuai dengan filter.
                      </td>
                    </tr>
                  ) : (
                    filteredApplications.map((app) => (
                      <tr key={app.id} onClick={() => openApplicantModal(app)} className="adm-clickable-row">
                        <td>
                          <strong className="adm-mono">{app.registration_no}</strong>
                          {app.student_id_number && (
                            <div className="adm-nis-tag">NIS: {app.student_id_number}</div>
                          )}
                        </td>
                        <td>
                          <strong>{app.child_full_name}</strong>
                          <div className="adm-sub-text">
                            {app.level_name} ({app.grade_target})
                            {app.sibling_order > 1 && ' • Saudara Kandung (Sibling)'}
                          </div>
                        </td>
                        <td>
                          <strong>{app.applicant_name}</strong>
                          <div className="adm-sub-text">{app.applicant_phone}</div>
                        </td>
                        <td>
                          <div>{app.campus_location.split(' - ')[0]}</div>
                          <div className="adm-sub-text">
                            <strong>T.A {app.academic_year || '2026/2027'}</strong> • {app.period_name}
                          </div>
                          {app.is_rollover_period && (
                            <span className="adm-rollover-tag" title={app.rollover_reason || 'Rollover dari kuota periode sebelumnya'}>
                              Rollover
                            </span>
                          )}
                        </td>
                        <td>{getStatusBadge(app.status_code)}</td>
                        <td>
                          {app.test_schedule ? (
                            <div>
                              <strong>{formatDateId(app.test_schedule.test_date)}</strong>
                              <div className="adm-sub-text">
                                {app.test_score !== null ? `Nilai: ${app.test_score}` : app.test_schedule.test_time}
                              </div>
                            </div>
                          ) : (
                            <span className="adm-sub-text">Belum dijadwalkan</span>
                          )}
                        </td>
                        <td>
                          {app.invoice ? (
                            <div>
                              <strong>{formatCurrency(app.invoice.total_payable)}</strong>
                              <div className="adm-sub-text">
                                {app.payment_proof_url
                                  ? 'Bukti Bayar Ada'
                                  : app.payment_method === 'direct_school'
                                  ? 'Bayar di Sekolah'
                                  : 'Belum Upload'}
                              </div>
                            </div>
                          ) : (
                            <span className="adm-sub-text">-</span>
                          )}
                        </td>
                        <td onClick={(e) => e.stopPropagation()}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                            <button
                              type="button"
                              className="adm-btn-sm"
                              onClick={() =>
                                openApplicantModal(
                                  app,
                                  app.status_code === 'payment_submitted'
                                    ? 'payment'
                                    : app.status_code === 'test_scheduled'
                                    ? 'test'
                                    : 'docs'
                                )
                              }
                            >
                              Kelola Tahapan
                            </button>
                            {(app.applicant_phone || app.parent_info?.phone) && (
                              <a
                                href={
                                  (app.whatsapp_urls && (
                                    app.status_code === 'enrolled'
                                      ? app.whatsapp_urls.enrolled
                                      : app.status_code === 'test_passed'
                                      ? app.whatsapp_urls.passed
                                      : app.status_code === 'test_scheduled'
                                      ? app.whatsapp_urls.test_scheduled
                                      : app.whatsapp_urls.general
                                  )) ||
                                  `https://wa.me/${(app.applicant_phone || app.parent_info?.phone || '').replace(/[^\d]/g, '')}?text=${encodeURIComponent(`Halo Bapak/Ibu ${app.applicant_name}, kami dari Tim Admissions Sekolah Permata Harapan mengenai pendaftaran ananda ${app.child_full_name}.`)}`
                                }
                                target="_blank"
                                rel="noreferrer"
                                className="adm-btn-wa"
                                title="Kirim notifikasi pesan WhatsApp otomatis ke orang tua"
                              >
                                <MessageSquare size={13} />
                                <span>WhatsApp</span>
                              </a>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ===================================================================
            TAB 3A: ADMISSION PERIODS & QUOTAS (TABLE + MODAL)
        =================================================================== */}
        {activeTab === 'periods' && (
          <div className="adm-tab-panel">
            <div className="adm-panel-head">
              <div>
                <h2>Periode Pendaftaran &amp; Kuota Jenjang</h2>
                <p>
                  Atur tanggal buka/tutup gelombang pendaftaran dan kuota kursi setiap jenjang. Perubahan otomatis diajukan ke Direktur Sekolah (School Director).
                </p>
              </div>
              <button
                type="button"
                className="adm-btn-add-primary"
                onClick={() => {
                  setEditingPeriod(null);
                  setPeriodForm({
                    period_name: '',
                    academic_year: '2026/2027',
                    start_date: '',
                    end_date: '',
                    quotas: { preschool: 25, kindergarten: 30, primary: 60, secondary: 45, sma: 0 },
                    is_active: true,
                    next_period_id: '',
                    notes: '',
                  });
                  setIsPeriodModalOpen(true);
                }}
              >
                <Plus size={16} />
                <span>Tambah Periode Pendaftaran</span>
              </button>
            </div>

            <div className="adm-table-card">
              <div className="adm-data-table-wrap">
                <table className="adm-data-table">
                  <thead>
                    <tr>
                      <th>Nama Gelombang / Periode</th>
                      <th>Tahun Ajaran</th>
                      <th>Rentang Tanggal</th>
                      <th>Alokasi Kuota Kursi</th>
                      <th>Status</th>
                      <th style={{ textAlign: 'right' }}>Aksi</th>
                    </tr>
                  </thead>
                  <tbody>
                    {displayPeriods.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="adm-empty-cell">
                          Belum ada periode pendaftaran. Klik tombol "+ Tambah Periode Pendaftaran" di atas untuk membuat gelombang baru.
                        </td>
                      </tr>
                    ) : (
                      displayPeriods.map((p) => {
                        const isPending = !!p.is_pending_approval;
                        const totalApplicantsInWave = p.total_applicants !== undefined ? p.total_applicants : (typeof p.filled === 'object' && p.filled ? Object.values(p.filled).reduce((a, b) => a + Number(b), 0) : Number(p.filled || p.applicant_count || 0));

                        return (
                          <tr key={p.id} style={isPending ? { background: '#fffbeb' } : {}}>
                            <td>
                              <strong>{p.period_name}</strong>
                              {isPending && (
                                <div style={{ fontSize: 11.5, color: '#b45309', fontWeight: 700, marginTop: 3 }}>
                                  Pengajuan gelombang baru menanti persetujuan Direktur Sekolah (School Director).
                                </div>
                              )}
                              {p.has_pending_update && (
                                <div style={{ fontSize: 11.5, color: '#b45309', fontWeight: 600, marginTop: 3 }}>
                                  Usulan revisi periode sedang menunggu persetujuan Direktur Sekolah (School Director).
                                </div>
                              )}
                              {p.notes && <div className="adm-sub-text">{p.notes}</div>}
                            </td>
                            <td>
                              <span className="adm-mono">{p.academic_year}</span>
                            </td>
                            <td>
                              {formatDateId(p.start_date)} s/d {formatDateId(p.end_date)}
                            </td>
                            <td>
                              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                                <span style={{ fontSize: 11.5, background: '#e0f2fe', color: '#0369a1', padding: '2px 6px', borderRadius: 4, fontWeight: 600 }}>
                                  PS: {p.quotas?.preschool ?? 0}
                                </span>
                                <span style={{ fontSize: 11.5, background: '#fef3c7', color: '#b45309', padding: '2px 6px', borderRadius: 4, fontWeight: 600 }}>
                                  KG: {p.quotas?.kindergarten ?? 0}
                                </span>
                                <span style={{ fontSize: 11.5, background: '#f3e8ff', color: '#6b21a8', padding: '2px 6px', borderRadius: 4, fontWeight: 600 }}>
                                  PRI: {p.quotas?.primary ?? 0}
                                </span>
                                <span style={{ fontSize: 11.5, background: '#ecfdf5', color: '#047857', padding: '2px 6px', borderRadius: 4, fontWeight: 600 }}>
                                  SEC: {p.quotas?.secondary ?? 0}
                                </span>
                              </div>
                            </td>
                            <td>
                              {isPending ? (
                                <span className="adm-status-pill adm-badge-amber" style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                                  <Clock size={12} />
                                  <span>Menunggu Approval</span>
                                </span>
                              ) : p.has_pending_update ? (
                                <span className="adm-status-pill adm-badge-amber" style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                                  <Clock size={12} />
                                  <span>Revisi Diajukan</span>
                                </span>
                              ) : p.has_pending_delete ? (
                                <span className="adm-status-pill adm-badge-red" style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                                  <Clock size={12} />
                                  <span>Usulan Hapus</span>
                                </span>
                              ) : p.is_active ? (
                                <span className="adm-status-pill adm-badge-green">Aktif</span>
                              ) : (
                                <span className="adm-status-pill adm-badge-gray">Nonaktif</span>
                              )}
                            </td>
                            <td>
                              <div className="adm-cell-actions" style={{ justifyContent: 'flex-end' }}>
                                <button
                                  type="button"
                                  className="adm-btn-action-view"
                                  onClick={() => setDetailPeriodModal(p)}
                                  title="Lihat detail lengkap gelombang, kuota & kelola"
                                >
                                  <Eye size={13} />
                                  <span>Lihat Detail</span>
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* ===================================================================
            TAB 3B: TEST SLOTS & SESSIONS (TABLE + MODAL)
        =================================================================== */}
        {activeTab === 'test-slots' && (
          <div className="adm-tab-panel">
            <div className="adm-panel-head">
              <div>
                <h2>Jadwal Sesi Tes Offline &amp; Observasi</h2>
                <p>
                  Kelola sesi tes observasi &amp; akademik offline. Admin Admissions menentukan penempatan sesi tes calon siswa saat validasi berkas pendaftar sesuai ketersediaan kuota ruangan.
                </p>
              </div>
              <button
                type="button"
                className="adm-btn-add-primary"
                onClick={() => {
                  setEditingSlot(null);
                  setSlotForm({
                    period_id: overview.periods?.[0]?.id || 1,
                    level_code: 'all',
                    session_name: '',
                    test_date: '',
                    test_time: '09:00 - 11:00 WIB',
                    campus_location: 'Campus 1 (Pos Pengumben) - Jl. Pos Pengumben Lama No.13',
                    room_name: 'Gedung Utama Lt. 2 - Ruang Assessment A',
                    slot_quota: 15,
                    custom_notes: '',
                  });
                  setIsSlotModalOpen(true);
                }}
              >
                <Plus size={16} />
                <span>Tambah Jadwal Sesi Tes</span>
              </button>
            </div>

            <div className="adm-table-card">
              <div className="adm-data-table-wrap">
                <table className="adm-data-table">
                  <thead>
                    <tr>
                      <th>Nama Sesi Tes &amp; Ruangan</th>
                      <th>Jenjang Target</th>
                      <th>Jadwal Pelaksanaan</th>
                      <th>Status &amp; Kapasitas</th>
                      <th style={{ textAlign: 'right' }}>Aksi</th>
                    </tr>
                  </thead>
                  <tbody>
                    {displaySlots.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="adm-empty-cell">
                          Belum ada sesi jadwal tes. Klik tombol "+ Tambah Jadwal Sesi Tes" di atas.
                        </td>
                      </tr>
                    ) : (
                      displaySlots.map((sl) => {
                        const isPending = !!sl.is_pending_approval;
                        const isFull = Number(sl.booked_count || 0) >= Number(sl.slot_quota || 0);
                        return (
                          <tr key={sl.id} style={isPending ? { background: '#fffbeb' } : {}}>
                            <td>
                              <strong>{sl.session_name}</strong>
                              {isPending && (
                                <div style={{ fontSize: 11.5, color: '#b45309', fontWeight: 600, marginTop: 3 }}>
                                  Pengajuan sesi baru menanti persetujuan Kepala Sekolah (Principal).
                                </div>
                              )}
                              {sl.has_pending_update && (
                                <div style={{ fontSize: 11.5, color: '#b45309', fontWeight: 600, marginTop: 3 }}>
                                  Usulan revisi sesi sedang menunggu persetujuan Principal.
                                </div>
                              )}
                              <div className="adm-sub-text">
                                {sl.campus_location?.split(' - ')[0]} &bull; {sl.room_name || '-'}
                              </div>
                              {sl.custom_notes && <div className="adm-sub-text" style={{ fontStyle: 'italic' }}>{sl.custom_notes}</div>}
                            </td>
                            <td>
                              <span className="adm-mono">
                                {sl.level_code === 'all' ? 'Semua Jenjang' : sl.level_code.toUpperCase()}
                              </span>
                            </td>
                            <td>
                              <div>
                                <strong>{formatDateId(sl.test_date)}</strong>
                                <div className="adm-sub-text">{sl.test_time}</div>
                              </div>
                            </td>
                            <td>
                              {isPending ? (
                                <span className="adm-status-pill adm-badge-amber" style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                                  <Clock size={12} />
                                  <span>Menunggu Approval</span>
                                </span>
                              ) : sl.has_pending_update ? (
                                <span className="adm-status-pill adm-badge-amber" style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                                  <Clock size={12} />
                                  <span>Revisi Diajukan</span>
                                </span>
                              ) : sl.has_pending_delete ? (
                                <span className="adm-status-pill adm-badge-red" style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                                  <Clock size={12} />
                                  <span>Usulan Hapus</span>
                                </span>
                              ) : isFull ? (
                                <span className="adm-status-pill adm-badge-red">Penuh</span>
                              ) : (
                                <span className="adm-status-pill adm-badge-green">Aktif</span>
                              )}
                              <div style={{ marginTop: 4 }}>
                                <span className={`adm-cap-badge ${isFull ? 'full' : 'avail'}`}>
                                  {sl.booked_count} / {sl.slot_quota} Peserta
                                </span>
                              </div>
                            </td>
                            <td>
                              <div className="adm-cell-actions" style={{ justifyContent: 'flex-end' }}>
                                <button
                                  type="button"
                                  className="adm-btn-action-view"
                                  onClick={() => setDetailSlotModal(sl)}
                                  title="Lihat detail lengkap, daftar hadir & kelola sesi tes"
                                >
                                  <Eye size={13} />
                                  <span>Lihat Detail</span>
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* ===================================================================
            TAB 4: MASTER LEVELS, ASSESSMENT & FEES (TABLE + MODAL)
        =================================================================== */}
        {activeTab === 'levels-fees' && (
          <div className="adm-tab-panel">
            <div className="adm-panel-head">
              <div>
                <h2>Master Jenjang Pendidikan, Keterangan Asesmen &amp; Standar Biaya</h2>
                <p>
                  Atur deskripsi materi seleksi tiap jenjang, status pendaftaran (Preschool s/d Secondary aktif, SMA Coming Soon), serta komponen biaya resmi. Perubahan tarif biaya otomatis diajukan ke Direktur Sekolah (School Director) untuk persetujuan.
                </p>
              </div>
            </div>

            <div className="adm-table-card">
              <div className="adm-data-table-wrap">
                <table className="adm-data-table">
                  <thead>
                    <tr>
                      <th>Jenjang Pendidikan</th>
                      <th>Reg. &amp; Test Fee</th>
                      <th>Admission Fee</th>
                      <th>Development Fee (DF)</th>
                      <th>SPP Bulanan</th>
                      <th>Status Approval Direktur Sekolah</th>
                      <th style={{ textAlign: 'right' }}>Aksi</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(overview.levels || []).map((lvl) => (
                      <tr key={lvl.level_code}>
                        <td>
                          <strong>{lvl.level_name}</strong>
                          <div className="adm-sub-text">
                            {lvl.tag_code} &bull; {lvl.assessment_title || 'Asesmen Standar'}
                            {!lvl.is_open && (
                              <span style={{ color: '#d97706', fontWeight: 600, marginLeft: 6 }}>
                                &bull; Belum Dibuka
                              </span>
                            )}
                          </div>
                        </td>
                        <td>
                          <div>
                            <strong>{formatCurrency(Number(lvl.registration_fee || 0) + Number(lvl.test_reg_form_fee || 0))}</strong>
                            <div className="adm-sub-text">Reg: {formatCurrency(lvl.registration_fee)} | Tes: {formatCurrency(lvl.test_reg_form_fee)}</div>
                          </div>
                        </td>
                        <td>
                          <strong>{formatCurrency(lvl.admission_fee)}</strong>
                        </td>
                        <td>
                          <div>
                            <strong>{formatCurrency(lvl.development_fee)}</strong>
                            <div className="adm-sub-text">DF1: {formatCurrency(lvl.df_1_amount)}</div>
                          </div>
                        </td>
                        <td>
                          <strong>{formatCurrency(lvl.tuition_fee)}</strong>
                        </td>
                        <td>
                          {lvl.fee_approval_status === 'approved' && (
                            <span className="adm-status-pill adm-badge-green">Disetujui Direktur Sekolah</span>
                          )}
                          {(lvl.fee_approval_status === 'pending_director' || lvl.fee_approval_status === 'pending_principal' || lvl.fee_approval_status === 'pending') && (
                            <span className="adm-status-pill adm-badge-orange">Menunggu Approval Direktur</span>
                          )}
                          {lvl.fee_approval_status === 'rejected' && (
                            <span className="adm-status-pill adm-badge-red">Ditolak (Perlu Penyesuaian)</span>
                          )}
                          {(!lvl.fee_approval_status || lvl.fee_approval_status === 'draft') && (
                            <span className="adm-status-pill adm-badge-gray">Draft</span>
                          )}
                        </td>
                        <td>
                          <div className="adm-cell-actions" style={{ justifyContent: 'flex-end' }}>
                            <button
                              type="button"
                              className="adm-btn-action-edit"
                              onClick={() => {
                                setSelectedLevelCfg(lvl);
                                setIsFeeModalOpen(true);
                              }}
                            >
                              <Edit3 size={13} />
                              <span>Ubah Biaya &amp; Asesmen</span>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* ===================================================================
            TAB: KURIKULUM MATERI ASESMEN & KARTU TES (PER JENJANG)
        =================================================================== */}
        {activeTab === 'assessment-curriculum' && (
          <AssessmentCurriculumManager
            apiBase={apiBase}
            onConfigUpdated={() => loadAll()}
          />
        )}

        {/* ===================================================================
            TAB 5: PROMO CODES & DISCOUNTS (TABLE + MODAL)
        =================================================================== */}
        {activeTab === 'promo-codes' && (
          <div className="adm-tab-panel">
            <div className="adm-panel-head">
              <div>
                <h2>Kode Voucher Diskon &amp; Promo Pendaftaran</h2>
                <p>Kode promo untuk Open House atau beasiswa potongan Development Fee.</p>
              </div>
              <button
                type="button"
                className="adm-btn-add-primary"
                onClick={() => {
                  setPromoForm({
                    promo_code: '',
                    promo_title: '',
                    discount_type: 'fixed',
                    discount_value: '',
                    applies_to_levels: 'all',
                    max_quota: 100,
                    valid_until: '2027-06-30',
                  });
                  setIsPromoModalOpen(true);
                }}
              >
                <Plus size={16} />
                <span>Buat Kode Promo Baru</span>
              </button>
            </div>

            <div className="adm-table-card">
              <div className="adm-data-table-wrap">
                <table className="adm-data-table">
                  <thead>
                    <tr>
                      <th>Kode &amp; Program Promo</th>
                      <th>Besaran Potongan</th>
                      <th>Berlaku Jenjang</th>
                      <th>Masa Berlaku</th>
                      <th>Status &amp; Kuota</th>
                      <th style={{ textAlign: 'right' }}>Aksi</th>
                    </tr>
                  </thead>
                  <tbody>
                    {displayPromos.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="adm-empty-cell">
                          Belum ada kode promo aktif. Klik tombol "+ Buat Kode Promo Baru" di atas.
                        </td>
                      </tr>
                    ) : (
                      displayPromos.map((pr) => {
                        const isPending = !!pr.is_pending_approval;
                        return (
                          <tr key={pr.id} style={isPending ? { background: '#fffbeb' } : {}}>
                            <td>
                              <strong className="adm-mono" style={{ color: '#2563eb' }}>{pr.promo_code}</strong>
                              <div style={{ fontWeight: 600, color: '#1e293b', marginTop: 2 }}>{pr.promo_title}</div>
                              {isPending && (
                                <div style={{ fontSize: 11.5, color: '#b45309', fontWeight: 600, marginTop: 3 }}>
                                  Pengajuan voucher baru menanti persetujuan Direktur Sekolah (School Director).
                                </div>
                              )}
                              {pr.has_pending_update && (
                                <div style={{ fontSize: 11.5, color: '#b45309', fontWeight: 600, marginTop: 3 }}>
                                  Usulan revisi voucher sedang menunggu persetujuan Direktur Sekolah (School Director).
                                </div>
                              )}
                            </td>
                            <td>
                              <span className="adm-mono" style={{ fontWeight: 700, color: '#059669' }}>
                                {pr.discount_type === 'percentage'
                                  ? `${pr.discount_value}% dari DF`
                                  : formatCurrency(pr.discount_value)}
                              </span>
                            </td>
                            <td>
                              <span className="adm-mono">{pr.applies_to_levels === 'all' ? 'Semua Jenjang' : pr.applies_to_levels.toUpperCase()}</span>
                            </td>
                            <td>
                              {formatDateId(pr.valid_until)}
                            </td>
                            <td>
                              {isPending ? (
                                <span className="adm-status-pill adm-badge-amber" style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                                  <Clock size={12} />
                                  <span>Menunggu Approval</span>
                                </span>
                              ) : pr.has_pending_update ? (
                                <span className="adm-status-pill adm-badge-amber" style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                                  <Clock size={12} />
                                  <span>Revisi Diajukan</span>
                                </span>
                              ) : pr.has_pending_delete ? (
                                <span className="adm-status-pill adm-badge-red" style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                                  <Clock size={12} />
                                  <span>Usulan Hapus</span>
                                </span>
                              ) : (
                                <span className="adm-status-pill adm-badge-green">Aktif</span>
                              )}
                              <div className="adm-sub-text" style={{ marginTop: 3 }}>
                                {pr.used_count || 0} / {pr.max_quota} Kupon
                              </div>
                            </td>
                            <td>
                              <div className="adm-cell-actions" style={{ justifyContent: 'flex-end' }}>
                                <button
                                  type="button"
                                  className="adm-btn-action-view"
                                  onClick={() => handleOpenDetailPromo(pr)}
                                  title="Lihat detail lengkap & daftar pendaftar pengguna kode promo"
                                >
                                  <Eye size={13} />
                                  <span>Lihat Detail</span>
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* ===================================================================
            MODAL: APPLICANT WORKFLOW MANAGER (VALIDATE DOCS -> TEST -> PAYMENT -> ENROLLED)
        =================================================================== */}
        {selectedApp && (
          <div className="adm-modal-overlay" onClick={() => setSelectedApp(null)}>
            <div className="adm-modal-box" onClick={(e) => e.stopPropagation()}>
              <div className="adm-modal-header">
                <div>
                  <span className="adm-mono">{selectedApp.registration_no}</span> •{' '}
                  <span>{selectedApp.period_name}</span>
                  {selectedApp.sibling_order > 1 && (
                    <span className="adm-sibling-chip">
                      Pendaftaran Saudara Kandung dari Akun {selectedApp.applicant_name} (Sibling)
                    </span>
                  )}
                  <h2>
                    {selectedApp.child_full_name} — {selectedApp.level_name} ({selectedApp.grade_target})
                  </h2>
                  <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                    {(selectedApp.applicant_phone || selectedApp.parent_info?.phone) && (
                      <a
                        href={
                          (selectedApp.whatsapp_urls && (
                            selectedApp.status_code === 'enrolled'
                              ? selectedApp.whatsapp_urls.enrolled
                              : selectedApp.status_code === 'test_passed'
                              ? selectedApp.whatsapp_urls.passed
                              : selectedApp.status_code === 'test_scheduled'
                              ? selectedApp.whatsapp_urls.test_scheduled
                              : selectedApp.whatsapp_urls.general
                          )) ||
                          `https://wa.me/${(selectedApp.applicant_phone || selectedApp.parent_info?.phone || '').replace(/[^\d]/g, '')}?text=${encodeURIComponent(`Halo Bapak/Ibu ${selectedApp.applicant_name}, kami dari Tim Admissions Sekolah Permata Harapan mengenai ananda ${selectedApp.child_full_name}.`)}`
                        }
                        target="_blank"
                        rel="noreferrer"
                        className="adm-btn-wa"
                        style={{ padding: '5px 11px', fontSize: 12.5 }}
                        title="Kirim notifikasi pesan WhatsApp otomatis ke orang tua"
                      >
                        <MessageSquare size={14} />
                        <span>Kirim WhatsApp</span>
                      </a>
                    )}
                    {selectedApp.test_slot_id && (
                      <button
                        type="button"
                        className="adm-btn-action-edit"
                        style={{ padding: '5px 11px', fontSize: 12.5 }}
                        onClick={() => setPreviewTestCardApp(selectedApp)}
                        title="Pratinjau Kartu Tanda Peserta Tes & Observasi"
                      >
                        <Printer size={14} />
                        <span>Kartu Peserta Tes</span>
                      </button>
                    )}
                    {(selectedApp.status_code === 'test_passed' || selectedApp.status_code === 'payment_submitted' || selectedApp.status_code === 'enrolled') && (
                      <button
                        type="button"
                        className="adm-btn-action-edit"
                        style={{ padding: '5px 11px', fontSize: 12.5 }}
                        onClick={() => setPreviewLoaApp(selectedApp)}
                        title="Pratinjau Letter of Acceptance (LoA) Resmi"
                      >
                        <BookOpen size={14} />
                        <span>Letter of Acceptance (LoA)</span>
                      </button>
                    )}
                    {selectedApp.status_code === 'enrolled' && (
                      <>
                        <button
                          type="button"
                          className="adm-btn-action-edit"
                          style={{ padding: '5px 11px', fontSize: 12.5 }}
                          onClick={() => setPreviewCertApp(selectedApp)}
                          title="Pratinjau Surat Bukti Siswa Resmi & NIS"
                        >
                          <Award size={14} />
                          <span>Official Student Certificate</span>
                        </button>
                        <button
                          type="button"
                          className="adm-btn-action-edit"
                          style={{ padding: '5px 11px', fontSize: 12.5 }}
                          onClick={() => setPreviewReceiptApp(selectedApp)}
                          title="Pratinjau Kwitansi / Invoice Pelunasan Resmi"
                        >
                          <FileSpreadsheet size={14} />
                          <span>Official Receipt &amp; Invoice</span>
                        </button>
                      </>
                    )}
                  </div>
                </div>
                <button type="button" className="adm-modal-close" onClick={() => setSelectedApp(null)}>
                  <X size={20} />
                </button>
              </div>

              {/* Sub-Navigation for the 3 Core Admin Actions */}
              <div className="adm-subtabs">
                <button
                  type="button"
                  className={`adm-subtab-btn ${detailSubTab === 'docs' ? 'active' : ''}`}
                  onClick={() => setDetailSubTab('docs')}
                >
                  1. Biodata, Validasi Berkas & Jadwal Tes Offline
                </button>
                <button
                  type="button"
                  className={`adm-subtab-btn ${detailSubTab === 'test' ? 'active' : ''}`}
                  onClick={() => setDetailSubTab('test')}
                >
                  2. Input Hasil Tes, LoA & Rincian Biaya (Invoice)
                </button>
                <button
                  type="button"
                  className={`adm-subtab-btn ${detailSubTab === 'payment' ? 'active' : ''}`}
                  onClick={() => setDetailSubTab('payment')}
                >
                  3. Validasi Bukti Bayar & Pengesahan Siswa (NIS)
                </button>
              </div>

              {(() => {
                const isPassedOrLater = ['test_passed', 'payment_submitted', 'payment_revision', 'enrolled'].includes(selectedApp.status_code);
                const isAlreadyPassed = isPassedOrLater || ['tested_passed', 'test_passed', 'payment_pending', 'payment_submitted', 'payment_revision', 'enrolled'].includes(selectedApp.status_code) || selectedApp.test_result === 'passed';
                const isEnrolled = selectedApp.status_code === 'enrolled';
                return (
              <div className="adm-modal-body">
                {/* SUBTAB 1: BIODATA, DOCUMENTS & OFFLINE TEST SCHEDULING */}
                {detailSubTab === 'docs' && (
                  <div>
                    <div className="adm-info-grid">
                      <div>
                        <span>Nama Calon Siswa</span>
                        <strong>{selectedApp.child_full_name}</strong>
                      </div>
                      <div>
                        <span>Tempat, Tanggal Lahir</span>
                        <strong>
                          {selectedApp.birth_place}, {formatDateId(selectedApp.birth_date)}
                        </strong>
                      </div>
                      <div>
                        <span>Jenis Kelamin & Kewarganegaraan</span>
                        <strong>
                          {selectedApp.gender} • {selectedApp.nationality}
                        </strong>
                      </div>
                      <div>
                        <span>Sekolah Asal</span>
                        <strong>{selectedApp.previous_school || '-'}</strong>
                      </div>
                      <div>
                        <span>Kampus & Jalur Masuk</span>
                        <strong>
                          {selectedApp.campus_location} ({selectedApp.intake_type})
                        </strong>
                      </div>
                      <div>
                        <span>Ayah / Ibu & Kontak</span>
                        <strong>
                          {selectedApp.parent_info?.father_name || '-'} &{' '}
                          {selectedApp.parent_info?.mother_name || '-'} ({selectedApp.applicant_phone})
                        </strong>
                      </div>
                      <div style={{ gridColumn: '1 / -1' }}>
                        <span>Alamat Rumah Keluarga</span>
                        <strong>{selectedApp.parent_info?.home_address || '-'}</strong>
                      </div>
                    </div>

                    <h4 className="adm-sec-heading">Pemeriksaan Berkas Persyaratan yang Diunggah Orang Tua</h4>
                    <div className="adm-docs-check-list">
                      {Object.entries(docDecisionForm.documents || {}).map(([docKey, docObj]) => (
                        <div key={docKey} className="adm-doc-check-row">
                          <div>
                            <strong>{docObj?.label || docKey}</strong>
                            {docObj?.url ? (
                              <div>
                                <a
                                  href={resolveFileLink(docObj.url)}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="adm-file-link"
                                >
                                  Buka / Periksa File Berkas <ExternalLink size={13} />
                                </a>
                              </div>
                            ) : (
                              <div className="adm-missing-file">Belum diunggah oleh orang tua</div>
                            )}
                          </div>

                          <div className="adm-doc-controls">
                            <select
                              value={docObj?.status || 'pending'}
                              disabled={isPassedOrLater}
                              onChange={(e) =>
                                setDocDecisionForm({
                                  ...docDecisionForm,
                                  documents: {
                                    ...docDecisionForm.documents,
                                    [docKey]: { ...docObj, status: e.target.value },
                                  },
                                })
                              }
                            >
                              <option value="valid">Berkas Sesuai (Valid)</option>
                              <option value="revision">Perlu Revisi / Upload Ulang</option>
                              <option value="pending">Pending</option>
                            </select>
                            <input
                              type="text"
                              placeholder="Catatan revisi berkas (jika ada)"
                              value={docObj?.note || ''}
                              disabled={isPassedOrLater}
                              onChange={(e) =>
                                setDocDecisionForm({
                                  ...docDecisionForm,
                                  documents: {
                                    ...docDecisionForm.documents,
                                    [docKey]: { ...docObj, note: e.target.value },
                                  },
                                })
                              }
                            />
                          </div>
                        </div>
                      ))}
                    </div>

                    {isPassedOrLater ? (
                      <div className="adm-step-completed-banner">
                        <CheckCircle2 size={24} color="#16a34a" />
                        <div>
                          <h4>Tahap Validasi Berkas &amp; Penjadwalan Tes Telah Selesai</h4>
                          <p>
                            Calon siswa telah menyelesaikan tes dan berstatus <strong>{selectedApp.status_code === 'enrolled' ? 'RESMI MENJADI SISWA BARU (ENROLLED)' : 'LULUS SELEKSI — MENUNGGU PEMBAYARAN'}</strong>.
                            Jadwal tes telah terlaksana pada <strong>{selectedApp.test_date || '-'}</strong> ({selectedApp.test_session_name || 'Sesi Tes'}).
                            Formulir validasi berkas dan penjadwalan otomatis dikunci untuk menjaga integritas data administrasi.
                          </p>
                        </div>
                      </div>
                    ) : (
                      <form onSubmit={handleValidateDocsSubmit} className="adm-action-box">
                        <h4>Keputusan Validasi Berkas &amp; Penjadwalan Tes Offline</h4>
                        <div className="adm-grid-2">
                          <div className="adm-field">
                            <label>Keputusan Validasi Berkas *</label>
                            <select
                              value={docDecisionForm.decision}
                              onChange={(e) =>
                                setDocDecisionForm({ ...docDecisionForm, decision: e.target.value })
                              }
                            >
                              <option value="approve">
                                Berkas Lengkap &amp; Sesuai — Terbitkan Jadwal Tes Offline
                              </option>
                              <option value="revision">
                                Berkas Belum Sesuai — Minta Orang Tua Upload Ulang (Revisi)
                              </option>
                            </select>
                          </div>

                          {docDecisionForm.decision === 'approve' && (
                            <div className="adm-field">
                              <label>Pilih Sesi Jadwal Tes Offline * (Penempatan oleh Admin Admissions)</label>
                              <select
                                value={docDecisionForm.test_slot_id}
                                onChange={(e) =>
                                  setDocDecisionForm({ ...docDecisionForm, test_slot_id: e.target.value })
                                }
                                required
                                style={{ width: '100%', maxWidth: '100%' }}
                              >
                                <option value="">-- Pilih Sesi Tes yang Tersedia --</option>
                                {(overview.test_slots || []).map((sl) => {
                                  const isFull = (sl.booked_count || 0) >= sl.slot_quota;
                                  const remaining = Math.max(0, sl.slot_quota - (sl.booked_count || 0));
                                  return (
                                    <option key={sl.id} value={sl.id} disabled={isFull}>
                                      {sl.session_name} &bull; {formatDateId(sl.test_date)} ({sl.test_time}) &mdash; {isFull ? '[KUOTA PENUH]' : `[Sisa ${remaining} Kursi (${sl.booked_count}/${sl.slot_quota})]`}
                                    </option>
                                  );
                                })}
                              </select>
                              <span className="adm-currency-subhelp" style={{ marginTop: 3, display: 'block', whiteSpace: 'normal', lineHeight: 1.35 }}>
                                Sesi yang kuotanya penuh dinonaktifkan otomatis.
                              </span>
                            </div>
                          )}
                        </div>

                        <div className="adm-field" style={{ marginTop: 12 }}>
                          <label>Catatan untuk Orang Tua</label>
                          <input
                            type="text"
                            value={docDecisionForm.admin_note}
                            onChange={(e) =>
                              setDocDecisionForm({ ...docDecisionForm, admin_note: e.target.value })
                            }
                            placeholder="Contoh: Berkas lengkap. Silakan hadir 15 menit sebelum jadwal tes dimulai."
                          />
                        </div>

                        <div style={{ marginTop: 14, textAlign: 'right' }}>
                          <button
                            type="submit"
                            className="adm-btn-primary"
                            disabled={submittingDocs}
                            style={{ maxWidth: '100%', whiteSpace: 'normal', lineHeight: 1.35, padding: '10px 18px' }}
                          >
                            {submittingDocs
                              ? 'Menyimpan...'
                              : docDecisionForm.decision === 'approve'
                              ? 'Validasi Berkas & Terbitkan Jadwal Tes ke Portal Orang Tua'
                              : 'Kirim Permintaan Revisi Berkas'}
                          </button>
                        </div>
                      </form>
                    )}
                  </div>
                )}

                {/* SUBTAB 2: INPUT OFFLINE TEST RESULT, GENERATE LoA & INVOICE */}
                {detailSubTab === 'test' && (
                  <form onSubmit={handleTestResultSubmit}>
                    {isEnrolled && (
                      <div className="adm-step-completed-banner" style={{ marginBottom: 16 }}>
                        <CheckCircle2 size={24} color="#16a34a" />
                        <div>
                          <h4>Hasil Evaluasi Seleksi &amp; LoA Telah Final</h4>
                          <p>
                            Siswa telah menyelesaikan pelunasan administrasi dan resmi terdaftar dengan <strong>NIS: {selectedApp.student_id_number}</strong>.
                            Skor evaluasi ({selectedApp.test_score ?? testResultForm.score}/100) serta rincian biaya resmi telah dikunci secara permanen.
                          </p>
                        </div>
                      </div>
                    )}
                    <div className="adm-action-box">
                      <h4>Input Hasil Tes & Observasi Offline</h4>
                      <div className="adm-grid-2">
                        <div className="adm-field">
                          <label>Keputusan Hasil Seleksi *</label>
                          <select
                            value={testResultForm.status}
                            onChange={(e) => setTestResultForm({ ...testResultForm, status: e.target.value })}
                          >
                            <option value="passed">
                              LULUS SELEKSI (Terbitkan Letter of Acceptance & Rincian Biaya)
                            </option>
                            <option value="failed">TIDAK LULUS SELEKSI</option>
                          </select>
                        </div>

                        <div className="adm-field">
                          <label>Nilai Evaluasi / Skor Tes (Skala 0 - 100)</label>
                          <div className="adm-percentage-input-wrap">
                            <input
                              type="number"
                              min="0"
                              max="100"
                              value={testResultForm.score}
                              onChange={(e) => {
                                const val = e.target.value;
                                if (val === '' || (Number(val) >= 0 && Number(val) <= 100)) {
                                  setTestResultForm({ ...testResultForm, score: val });
                                }
                              }}
                              placeholder="Contoh: 88"
                            />
                            <span className="adm-input-suffix" style={{ fontSize: '0.8rem', fontWeight: 600 }}>/ 100</span>
                          </div>
                        </div>
                      </div>

                      <div className="adm-field" style={{ marginTop: 12 }}>
                        <label>Catatan Hasil Evaluasi / Pengumuman untuk Orang Tua *</label>
                        <textarea
                          rows={2}
                          value={testResultForm.evaluation_notes}
                          onChange={(e) =>
                            setTestResultForm({ ...testResultForm, evaluation_notes: e.target.value })
                          }
                          placeholder="Masukkan catatan hasil observasi/tes akademik siswa..."
                        />
                      </div>

                      <div className="adm-upload-card" style={{ marginTop: 14 }}>
                        <label>
                          Dokumen Scan Hasil Tes & Observasi Calon Siswa (PDF / JPG / PNG) *
                        </label>
                        <input
                          type="file"
                          accept=".pdf,.jpg,.jpeg,.png,.webp"
                          onChange={(e) => handleTestDocUpload(e.target.files?.[0])}
                        />
                        {uploadingTestDoc && <span className="adm-uploading">Mengunggah berkas...</span>}
                        {testResultForm.test_result_doc_url ? (
                          <div className="adm-doc-preview-link" style={{ marginTop: 6, display: 'flex', alignItems: 'center', gap: 8 }}>
                            <span style={{ color: '#166534', fontWeight: 600, fontSize: '0.85rem' }}>
                              Dokumen Hasil Tes Terlampir
                            </span>
                            <a
                              href={resolveFileLink(testResultForm.test_result_doc_url)}
                              target="_blank"
                              rel="noreferrer"
                              className="adm-file-link"
                            >
                              Lihat Dokumen Hasil Tes Terlampir (.pdf) <ExternalLink size={13} />
                            </a>
                          </div>
                        ) : (
                          <span className="adm-sub-text" style={{ display: 'block', marginTop: 4 }}>
                            Wajib melampirkan berkas scan/foto lembar hasil tes (.pdf) yang sah. Dokumen ini dapat diunduh oleh orang tua saat mencetak Kartu Tes & Hasil.
                          </span>
                        )}
                      </div>

                      {testResultForm.status === 'passed' && (
                        <>
                          <div className="adm-sec-header-row">
                            <h4 className="adm-sec-heading" style={{ margin: 0 }}>
                              Rincian Biaya Pendaftaran (Invoice) yang Akan Diterbitkan ke Orang Tua
                            </h4>
                            <span className="adm-badge-locked">
                              Standar Resmi Terkunci (Sesuai Master Biaya)
                            </span>
                          </div>
                          <p className="adm-fee-locked-note">
                            Nominal biaya dasar di bawah ini otomatis dikunci mengikuti standar resmi yang telah disetujui Direktur Sekolah pada Master Jenjang & Biaya untuk mencegah perubahan tarif di luar ketentuan.
                          </p>
                          <div className="adm-grid-3">
                            <div className="adm-field">
                              <label>Registration Fee (Rp) - Standar</label>
                              <input
                                type="text"
                                readOnly
                                className="adm-input-locked"
                                value={formatCurrency(testResultForm.registration_fee)}
                              />
                            </div>
                            <div className="adm-field">
                              <label>Test / Reg. Form Fee (Rp) - Standar</label>
                              <input
                                type="text"
                                readOnly
                                className="adm-input-locked"
                                value={formatCurrency(testResultForm.test_reg_form_fee)}
                              />
                            </div>
                            <div className="adm-field">
                              <label>Admission Fee (Rp) - Standar</label>
                              <input
                                type="text"
                                readOnly
                                className="adm-input-locked"
                                value={formatCurrency(testResultForm.admission_fee)}
                              />
                            </div>
                            <div className="adm-field">
                              <label>Development Fee (Rp) - Standar</label>
                              <input
                                type="text"
                                readOnly
                                className="adm-input-locked"
                                value={formatCurrency(testResultForm.development_fee)}
                              />
                            </div>
                            <div className="adm-field">
                              <label>Books & Activity Fee (Rp) - Standar</label>
                              <input
                                type="text"
                                readOnly
                                className="adm-input-locked"
                                value={formatCurrency(testResultForm.books_activity_fee)}
                              />
                            </div>
                            <div className="adm-field">
                              <label>Tuition Fee / SPP (Rp) - Standar</label>
                              <input
                                type="text"
                                readOnly
                                className="adm-input-locked"
                                value={formatCurrency(testResultForm.tuition_fee)}
                              />
                            </div>
                          </div>

                          <div className="adm-grid-2" style={{ marginTop: 12 }}>
                            <div className="adm-field">
                              <label>Potongan Diskon Resmi (Rp) - Terkunci</label>
                              <input
                                type="text"
                                readOnly
                                className="adm-input-locked"
                                value={formatCurrency(testResultForm.discount_amount || 0)}
                              />
                              <span className="adm-sub-text" style={{ marginTop: 4, display: 'block' }}>
                                Nominal diskon terkunci otomatis sesuai validasi kode promo resmi.
                              </span>
                            </div>
                            <div className="adm-field">
                              <label>Keterangan Diskon Resmi - Terkunci</label>
                              <input
                                type="text"
                                readOnly
                                className="adm-input-locked"
                                value={testResultForm.discount_label || '- Tidak Ada Diskon -'}
                              />
                              <span className="adm-sub-text" style={{ marginTop: 4, display: 'block' }}>
                                Rujukan diskon resmi yang akan tertera pada Letter of Acceptance (LoA) & Kwitansi.
                              </span>
                            </div>
                          </div>

                          <div className="adm-total-preview">
                            Total Tagihan Bersih Setelah Diskon:{' '}
                            <strong>
                              {formatCurrency(
                                Math.max(
                                  0,
                                  Number(testResultForm.registration_fee || 0) +
                                    Number(testResultForm.test_reg_form_fee || 0) +
                                    Number(testResultForm.admission_fee || 0) +
                                    Number(testResultForm.development_fee || 0) +
                                    Number(testResultForm.books_activity_fee || 0) +
                                    Number(testResultForm.tuition_fee || 0) -
                                    Number(testResultForm.discount_amount || 0)
                                )
                              )}
                            </strong>
                          </div>
                        </>
                      )}

                      {isAlreadyPassed ? (
                        <div className="adm-passed-status-banner">
                          <div className="adm-passed-status-content">
                            <CheckCircle2 size={24} className="adm-passed-icon" />
                            <div>
                              <h4 className="adm-passed-title">Status Seleksi: Calon Siswa Telah Dinyatakan LULUS</h4>
                              <p className="adm-passed-desc">
                                Surat penerimaan resmi (Letter of Acceptance) dan rincian biaya invoice telah diterbitkan ke portal orang tua.
                              </p>
                            </div>
                          </div>
                          <div className="adm-passed-actions">
                            <button
                              type="button"
                              className="adm-btn-preview-loa"
                              onClick={() => setPreviewLoaApp(selectedApp)}
                            >
                              <FileText size={15} />
                              <span>Pratinjau Letter of Acceptance (LoA)</span>
                            </button>
                            {!isEnrolled && (
                              <button
                                type="submit"
                                className="adm-btn-outline-sm"
                                disabled={submittingTest}
                              >
                                {submittingTest ? 'Menyimpan...' : 'Perbarui Catatan / Berkas Hasil'}
                              </button>
                            )}
                          </div>
                        </div>
                      ) : (
                        <div style={{ marginTop: 16, textAlign: 'right' }}>
                          <button type="submit" className="adm-btn-primary" disabled={submittingTest || isEnrolled}>
                            {submittingTest
                              ? 'Menyimpan...'
                              : isEnrolled
                              ? 'Hasil Seleksi Telah Terkunci (Siswa Resmi)'
                              : testResultForm.status === 'passed'
                              ? 'Umumkan Lulus, Terbitkan LoA & Rincian Biaya Pembayaran'
                              : 'Simpan Pengumuman Tidak Lulus'}
                          </button>
                        </div>
                      )}
                    </div>
                  </form>
                )}

                {/* SUBTAB 3: VERIFY PAYMENT PROOF & OFFICIAL ENROLLMENT */}
                {detailSubTab === 'payment' && (
                  <div>
                    <div className="adm-info-grid">
                      <div>
                        <span>Kode Pembayaran Unik</span>
                        <strong className="adm-mono">{selectedApp.invoice?.payment_code || '-'}</strong>
                      </div>
                      <div>
                        <span>Total Tagihan Invoice</span>
                        <strong>{formatCurrency(selectedApp.invoice?.total_payable || 0)}</strong>
                      </div>
                      <div>
                        <span>Metode Pembayaran Dipilih Ortu</span>
                        <strong>
                          {selectedApp.payment_method === 'direct_school'
                            ? 'Bayar Langsung ke Sekolah (Kasir)'
                            : selectedApp.payment_method
                            ? 'Transfer Rekening / Kode Bayar'
                            : 'Belum Konfirmasi'}
                        </strong>
                      </div>
                      <div>
                        <span>Nominal Dibayar yang Diinput Ortu</span>
                        <strong>
                          {selectedApp.payment_amount_paid
                            ? formatCurrency(selectedApp.payment_amount_paid)
                            : '-'}
                        </strong>
                      </div>
                      <div>
                        <span>Catatan Pembayaran Orang Tua</span>
                        <strong>{selectedApp.payment_notes || '-'}</strong>
                      </div>
                      <div>
                        <span>File Bukti Pembayaran</span>
                        {selectedApp.payment_proof_url ? (
                          <a
                            href={resolveFileLink(selectedApp.payment_proof_url)}
                            target="_blank"
                            rel="noreferrer"
                            className="adm-file-link"
                          >
                            Buka / Periksa Bukti Pembayaran <ExternalLink size={14} />
                          </a>
                        ) : (
                          <span className="adm-missing-file">Belum ada file bukti transfer</span>
                        )}
                      </div>
                    </div>

                    {isEnrolled ? (
                      <div className="adm-step-completed-banner" style={{ marginTop: 18 }}>
                        <CheckCircle2 size={24} color="#16a34a" />
                        <div>
                          <h4>Pembayaran Telah Diverifikasi Lunas &amp; Siswa Resmi Terdaftar</h4>
                          <p>
                            Ananda <strong>{selectedApp.child_full_name}</strong> telah resmi tercatat sebagai Siswa Sekolah Permata Harapan dengan Nomor Induk Siswa: <strong>{selectedApp.student_id_number}</strong>.
                            Dokumen resmi <em>Certificate of Enrollment</em> dan <em>Official Receipt</em> dapat diunduh pada tombol pratinjau di bagian atas modal ini atau oleh orang tua di Portal PPDB.
                          </p>
                        </div>
                      </div>
                    ) : (
                      <form onSubmit={handleVerifyPaymentSubmit} className="adm-action-box" style={{ marginTop: 18 }}>
                        <h4>Validasi Pembayaran &amp; Pengesahan Resmi Siswa Baru</h4>
                        <div className="adm-grid-2">
                          <div className="adm-field">
                            <label>Keputusan Verifikasi Pembayaran *</label>
                            <select
                              value={paymentVerifyForm.decision}
                              onChange={(e) =>
                                setPaymentVerifyForm({ ...paymentVerifyForm, decision: e.target.value })
                              }
                            >
                              <option value="verify">
                                Bukti Bayar Sesuai &amp; Lunas — Sahkan Anak Menjadi Siswa Resmi (Enrolled)
                              </option>
                              <option value="revision">
                                Bukti Bayar Belum Sesuai — Minta Orang Tua Upload Ulang Bukti Bayar
                              </option>
                            </select>
                          </div>

                          {paymentVerifyForm.decision === 'verify' && (
                            <div className="adm-field">
                              <label>Nomor Induk Siswa (NIS) Resmi *</label>
                              <input
                                type="text"
                                value={paymentVerifyForm.student_id_number}
                                onChange={(e) =>
                                  setPaymentVerifyForm({
                                    ...paymentVerifyForm,
                                    student_id_number: e.target.value,
                                  })
                                }
                                required
                              />
                            </div>
                          )}
                        </div>

                        {paymentVerifyForm.decision === 'verify' && (
                          <div className="adm-receipt-auto-card" style={{ marginTop: 14 }}>
                            <CheckCircle2 size={20} color="#059669" />
                            <div className="adm-receipt-auto-text">
                              <strong>Kwitansi Resmi (Official Receipt) &amp; NIS Diterbitkan Otomatis</strong>
                              <p>
                                Sistem secara otomatis menerbitkan Kwitansi Resmi berpenomoran sekuensial dengan tanda tangan PIC Keuangan resmi, stempel verifikasi, rincian biaya bersih, serta QR Code verifikasi dokumen. Dokumen ini dapat diunduh langsung oleh sekolah dan orang tua di Portal PPDB.
                              </p>
                            </div>
                          </div>
                        )}

                        <div className="adm-field" style={{ marginTop: 12 }}>
                          <label>Catatan Pengesahan / Kwitansi untuk Orang Tua</label>
                          <input
                            type="text"
                            value={paymentVerifyForm.admin_note}
                            onChange={(e) =>
                              setPaymentVerifyForm({ ...paymentVerifyForm, admin_note: e.target.value })
                            }
                            placeholder="Contoh: Pembayaran telah diterima lunas. Selamat bergabung di Sekolah Permata Harapan!"
                          />
                        </div>

                        <div style={{ marginTop: 16, textAlign: 'right' }}>
                          <button type="submit" className="adm-btn-primary" disabled={submittingVerify}>
                            {submittingVerify
                              ? 'Memproses...'
                              : paymentVerifyForm.decision === 'verify'
                              ? 'Verifikasi Lunas & Sahkan Menjadi Siswa Sekolah Permata Harapan'
                              : 'Kirim Permintaan Revisi Bukti Bayar'}
                          </button>
                        </div>
                      </form>
                    )}
                  </div>
                )}
              </div>
                );
              })()}
            </div>
          </div>
        )}

        {/* ===================================================================
            MODAL 1: ADD / EDIT ADMISSION PERIOD & QUOTAS
        =================================================================== */}
        {isPeriodModalOpen && (
          <div className="adm-config-modal-overlay" onClick={() => setIsPeriodModalOpen(false)}>
            <div className="adm-config-modal-box" style={{ maxWidth: 740 }} onClick={(e) => e.stopPropagation()}>
              <div className="adm-config-modal-head">
                <div className="adm-config-modal-head-title">
                  <div className="adm-config-modal-head-icon">
                    <Calendar size={18} />
                  </div>
                  <div>
                    <h3>{editingPeriod ? 'Edit Periode & Kuota Pendaftaran' : 'Tambah Periode Pendaftaran Baru'}</h3>
                    <p style={{ margin: 0, fontSize: '0.78rem', color: '#94a3b8' }}>
                      Atur nama gelombang, tahun ajaran, durasi pendaftaran, dan alokasi kuota per jenjang.
                    </p>
                  </div>
                </div>
                <button type="button" onClick={() => setIsPeriodModalOpen(false)} title="Tutup Modal">
                  <X size={18} />
                </button>
              </div>
              <form onSubmit={handleSavePeriod} style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
                <div className="adm-config-modal-body">
                  <div className="adm-form-card-section">
                    <div className="adm-form-section-head">
                      <Sparkles size={15} color="#0284c7" />
                      <span>Informasi Dasar Gelombang &amp; Jadwal</span>
                    </div>
                    <div className="adm-field">
                      <label>Nama Gelombang / Periode Pendaftaran *</label>
                      <input
                        type="text"
                        value={periodForm.period_name}
                        onChange={(e) => setPeriodForm({ ...periodForm, period_name: e.target.value })}
                        placeholder="Contoh: Gelombang 1 - TA 2026/2027"
                        required
                      />
                    </div>

                    <div className="adm-grid-3" style={{ marginTop: 10 }}>
                      <div className="adm-field">
                        <label>Tahun Ajaran *</label>
                        <input
                          type="text"
                          value={periodForm.academic_year}
                          onChange={(e) => setPeriodForm({ ...periodForm, academic_year: e.target.value })}
                          placeholder="2026/2027"
                          required
                        />
                      </div>
                      <div className="adm-field">
                        <label>Tanggal Buka *</label>
                        <input
                          type="date"
                          value={periodForm.start_date}
                          onChange={(e) => setPeriodForm({ ...periodForm, start_date: e.target.value })}
                          required
                        />
                      </div>
                      <div className="adm-field">
                        <label>Tanggal Tutup *</label>
                        <input
                          type="date"
                          value={periodForm.end_date}
                          onChange={(e) => setPeriodForm({ ...periodForm, end_date: e.target.value })}
                          required
                        />
                      </div>
                    </div>
                  </div>

                  {/* KARTU KUOTA DENGAN STEPPER INTERAKTIF */}
                  <div className="adm-form-card-section">
                    <div className="adm-form-section-head">
                      <Layers size={15} color="#0284c7" />
                      <span>Alokasi Kuota Kursi Tiap Jenjang (Interaktif)</span>
                      <span className="adm-form-section-head-badge">Gunakan [-] / [+] atau ketik langsung</span>
                    </div>
                    <div className="adm-grid-4">
                      {[
                        { key: 'preschool', name: 'Preschool', tag: 'PS', bg: '#ecfdf5', color: '#047857' },
                        { key: 'kindergarten', name: 'Kindergarten', tag: 'KG', bg: '#e0f2fe', color: '#0284c7' },
                        { key: 'primary', name: 'Primary (SD)', tag: 'PRI', bg: '#eff6ff', color: '#1d4ed8' },
                        { key: 'secondary', name: 'Secondary (SMP)', tag: 'SEC', bg: '#f5f3ff', color: '#6d28d9' },
                      ].map((item) => (
                        <div key={item.key} className="adm-field">
                          <label style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <span>{item.name}</span>
                            <span style={{ fontSize: '0.7rem', fontWeight: 700, color: item.color }}>{item.tag}</span>
                          </label>
                          <QuantityStepperInput
                            value={periodForm.quotas[item.key] ?? 30}
                            onChange={(newVal) =>
                              setPeriodForm({
                                ...periodForm,
                                quotas: { ...periodForm.quotas, [item.key]: newVal },
                              })
                            }
                            unit="Kursi"
                            min={0}
                            quickAdds={[5, 10]}
                          />
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="adm-form-card-section">
                    <div className="adm-form-section-head">
                      <Settings size={15} color="#0284c7" />
                      <span>Status Operasional Gelombang</span>
                    </div>
                    <div className="adm-field">
                      <label>Status Periode Pendaftaran</label>
                      <select
                        value={periodForm.is_active ? '1' : '0'}
                        onChange={(e) => setPeriodForm({ ...periodForm, is_active: e.target.value === '1' })}
                      >
                        <option value="1">Aktif (Menerima Pendaftar)</option>
                        <option value="0">Nonaktif / Arsip</option>
                      </select>
                      <span className="adm-currency-subhelp" style={{ marginTop: 3 }}>
                        Jika kuota jenjang pada gelombang ini telah terpenuhi, pendaftar baru secara otomatis dialihkan ke gelombang buka terdekat berikutnya.
                      </span>
                    </div>

                    <div className="adm-field" style={{ marginTop: 12 }}>
                      <label>Catatan Internal Gelombang (Opsional)</label>
                      <input
                        type="text"
                        value={periodForm.notes || ''}
                        onChange={(e) => setPeriodForm({ ...periodForm, notes: e.target.value })}
                        placeholder="Contoh: Gelombang Early Bird dengan kuota promo khusus"
                      />
                    </div>
                  </div>
                </div>

                <div className="adm-config-modal-footer">
                  <button
                    type="button"
                    className="adm-btn-outline"
                    onClick={() => setIsPeriodModalOpen(false)}
                    disabled={isSavingPeriod}
                  >
                    Batal
                  </button>
                  <button type="submit" className="adm-btn-primary" disabled={isSavingPeriod}>
                    {isSavingPeriod ? 'Menyimpan...' : (editingPeriod ? 'Simpan Perubahan Periode' : 'Ajukan & Simpan Periode Baru')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* ===================================================================
            MODAL 2: ADD / EDIT TEST SLOTS & SESSIONS
        =================================================================== */}
        {isSlotModalOpen && (
          <div className="adm-config-modal-overlay" onClick={() => setIsSlotModalOpen(false)}>
            <div className="adm-config-modal-box" style={{ maxWidth: 740 }} onClick={(e) => e.stopPropagation()}>
              <div className="adm-config-modal-head">
                <div className="adm-config-modal-head-title">
                  <div className="adm-config-modal-head-icon">
                    <Clock size={18} />
                  </div>
                  <div>
                    <h3>{editingSlot ? 'Edit Sesi Jadwal Tes Offline' : 'Tambah Sesi Jadwal Tes Baru'}</h3>
                    <p style={{ margin: 0, fontSize: '0.78rem', color: '#94a3b8' }}>
                      Atur nama sesi, sasaran jenjang, waktu observasi, ruangan, dan batas kapasitas kuota peserta.
                    </p>
                  </div>
                </div>
                <button type="button" onClick={() => setIsSlotModalOpen(false)} title="Tutup Modal">
                  <X size={18} />
                </button>
              </div>
              <form onSubmit={handleCreateSlot} style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
                <div className="adm-config-modal-body">
                  <div className="adm-form-card-section">
                    <div className="adm-form-section-head">
                      <Layers size={15} color="#0284c7" />
                      <span>Identitas Sesi &amp; Sasaran Jenjang</span>
                    </div>
                    <div className="adm-grid-2">
                      <div className="adm-field">
                        <label>Nama Sesi Tes *</label>
                        <input
                          type="text"
                          value={slotForm.session_name}
                          onChange={(e) => setSlotForm({ ...slotForm, session_name: e.target.value })}
                          placeholder="Contoh: Sesi Tes & Observasi 1 (Sabtu Pagi)"
                          required
                        />
                      </div>
                      <div className="adm-field">
                        <label>Jenjang Target</label>
                        <select
                          value={slotForm.level_code}
                          onChange={(e) => setSlotForm({ ...slotForm, level_code: e.target.value })}
                        >
                          <option value="all">Semua Jenjang (All Levels)</option>
                          <option value="preschool">Preschool</option>
                          <option value="kindergarten">Kindergarten</option>
                          <option value="primary">Primary</option>
                          <option value="secondary">Secondary</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  <div className="adm-form-card-section">
                    <div className="adm-form-section-head">
                      <Calendar size={15} color="#0284c7" />
                      <span>Waktu Pelaksanaan &amp; Kapasitas Kuota Peserta</span>
                    </div>
                    <div className="adm-grid-3">
                      <div className="adm-field">
                        <label>Tanggal Pelaksanaan *</label>
                        <input
                          type="date"
                          value={slotForm.test_date}
                          onChange={(e) => setSlotForm({ ...slotForm, test_date: e.target.value })}
                          required
                        />
                      </div>
                      <div className="adm-field">
                        <label>Jam Pelaksanaan *</label>
                        <input
                          type="text"
                          value={slotForm.test_time}
                          onChange={(e) => setSlotForm({ ...slotForm, test_time: e.target.value })}
                          placeholder="09:00 - 11:00 WIB"
                          required
                        />
                      </div>
                      <div className="adm-field">
                        <label>Kapasitas Kuota Peserta *</label>
                        <QuantityStepperInput
                          value={slotForm.slot_quota || 20}
                          onChange={(val) => setSlotForm({ ...slotForm, slot_quota: val })}
                          unit="Peserta"
                          min={1}
                          quickAdds={[5, 10]}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="adm-form-card-section">
                    <div className="adm-form-section-head">
                      <MapPin size={15} color="#0284c7" />
                      <span>Lokasi Kampus &amp; Ruangan Tes</span>
                    </div>
                    <div className="adm-grid-2">
                      <div className="adm-field">
                        <label>Lokasi Kampus</label>
                        <select
                          value={slotForm.campus_location}
                          onChange={(e) => setSlotForm({ ...slotForm, campus_location: e.target.value })}
                        >
                          <option value="Campus 1 (Pos Pengumben) - Jl. Pos Pengumben Lama No.13">
                            Campus 1 (Pos Pengumben) - Jl. Pos Pengumben Lama No.13
                          </option>
                          <option value="Campus 2 (Srengseng) - Jl. Srengseng Raya No.69">
                            Campus 2 (Srengseng) - Jl. Srengseng Raya No.69
                          </option>
                        </select>
                      </div>
                      <div className="adm-field">
                        <label>Ruangan Tes / Observasi</label>
                        <input
                          type="text"
                          value={slotForm.room_name}
                          onChange={(e) => setSlotForm({ ...slotForm, room_name: e.target.value })}
                          placeholder="Contoh: Gedung Utama Lt. 2 - Ruang Assessment A"
                        />
                      </div>
                    </div>

                    <div className="adm-field" style={{ marginTop: 10 }}>
                      <label>Catatan Instruksi Sesi untuk Orang Tua (Opsional)</label>
                      <input
                        type="text"
                        value={slotForm.custom_notes}
                        onChange={(e) => setSlotForm({ ...slotForm, custom_notes: e.target.value })}
                        placeholder="Contoh: Harap hadir 15 menit sebelum observasi dimulai"
                      />
                    </div>
                  </div>
                </div>

                <div className="adm-config-modal-footer">
                  <button
                    type="button"
                    className="adm-btn-outline"
                    onClick={() => setIsSlotModalOpen(false)}
                    disabled={isSavingSlot}
                  >
                    Batal
                  </button>
                  <button type="submit" className="adm-btn-primary" disabled={isSavingSlot}>
                    {isSavingSlot ? 'Menyimpan...' : (editingSlot ? 'Simpan Perubahan Jadwal Sesi' : 'Ajukan & Simpan Sesi Tes')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* ===================================================================
            MODAL 3: EDIT LEVEL CONFIGURATION & FEE STRUCTURE (TABBED ENTERPRISE UI)
        =================================================================== */}
        {isFeeModalOpen && selectedLevelCfg && (
          <div className="adm-config-modal-overlay" onClick={() => setIsFeeModalOpen(false)}>
            <div className="adm-config-modal-box" style={{ maxWidth: 880 }} onClick={(e) => e.stopPropagation()}>
              <div className="adm-config-modal-head">
                <div className="adm-config-modal-head-title">
                  <div className="adm-config-modal-head-icon">
                    <GraduationCap size={18} />
                  </div>
                  <div>
                    <h3>
                      Pengaturan Jenjang &amp; Biaya: {selectedLevelCfg.level_name}
                      <span className="adm-config-modal-head-tag">{selectedLevelCfg.tag_code}</span>
                    </h3>
                    <p style={{ margin: 0, fontSize: '0.78rem', color: '#94a3b8' }}>
                      Kelola tarif uang masuk, termin cicilan, kurikulum tes observasi, dan pejabat penandatangan dokumen.
                    </p>
                  </div>
                </div>
                <button type="button" onClick={() => setIsFeeModalOpen(false)} title="Tutup Modal">
                  <X size={18} />
                </button>
              </div>

              {/* MODERN SUBNAV TABS */}
              <div className="adm-modal-subnav">
                <button
                  type="button"
                  className={`adm-modal-subnav-btn ${feeModalTab === 'fees' ? 'active' : ''}`}
                  onClick={() => setFeeModalTab('fees')}
                >
                  <CreditCard size={15} />
                  <span>1. Tarif Biaya &amp; Uang Pangkal</span>
                </button>
                <button
                  type="button"
                  className={`adm-modal-subnav-btn ${feeModalTab === 'status_mat' ? 'active' : ''}`}
                  onClick={() => setFeeModalTab('status_mat')}
                >
                  <BookOpen size={15} />
                  <span>2. Status &amp; Materi Tes Offline</span>
                </button>
                <button
                  type="button"
                  className={`adm-modal-subnav-btn ${feeModalTab === 'bank_sign' ? 'active' : ''}`}
                  onClick={() => setFeeModalTab('bank_sign')}
                >
                  <ShieldCheck size={15} />
                  <span>3. Rekening &amp; Pejabat Penandatangan</span>
                </button>
              </div>

              <form onSubmit={handleSaveLevelConfig} style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
                <div className="adm-config-modal-body">
                  {selectedLevelCfg.fee_approval_status === 'approved' && (
                    <div style={{ background: '#ecfdf5', border: '1.5px solid #a7f3d0', padding: '12px 16px', borderRadius: 10, marginBottom: 16, color: '#065f46', fontSize: 13, display: 'flex', alignItems: 'center', gap: 10 }}>
                      <CheckCircle2 size={18} color="#059669" style={{ flexShrink: 0 }} />
                      <div>
                        <strong>Status Tarif Biaya: Telah Disetujui Direktur Sekolah (School Director) &amp; Resmi Aktif</strong>
                        <p style={{ margin: '2px 0 0', fontSize: '0.8rem', color: '#047857' }}>
                          Biaya yang diubah di form ini akan otomatis diajukan kembali sebagai draf revisi ke Direktur Sekolah untuk ditinjau ulang.
                        </p>
                      </div>
                    </div>
                  )}
                  {(selectedLevelCfg.fee_approval_status === 'pending_director' || selectedLevelCfg.fee_approval_status === 'pending_principal' || selectedLevelCfg.fee_approval_status === 'pending') && (
                    <div style={{ background: '#fffbeb', border: '1.5px solid #fde68a', padding: '12px 16px', borderRadius: 10, marginBottom: 16, color: '#92400e', fontSize: 13, display: 'flex', alignItems: 'center', gap: 10 }}>
                      <AlertCircle size={18} color="#d97706" style={{ flexShrink: 0 }} />
                      <div>
                        <strong>Status: Menunggu Persetujuan Direktur Sekolah (School Director)</strong>
                        <p style={{ margin: '2px 0 0', fontSize: '0.8rem', color: '#b45309' }}>
                          Pengajuan perubahan biaya sedang dalam antrean verifikasi Direktur Sekolah sebelum diberlakukan kepada calon pendaftar baru.
                        </p>
                      </div>
                    </div>
                  )}
                  {selectedLevelCfg.fee_approval_status === 'rejected' && (
                    <div style={{ background: '#fef2f2', border: '1.5px solid #fecaca', padding: '12px 16px', borderRadius: 10, marginBottom: 16, color: '#991b1b', fontSize: 13, display: 'flex', alignItems: 'center', gap: 10 }}>
                      <AlertTriangle size={18} color="#dc2626" style={{ flexShrink: 0 }} />
                      <div>
                        <strong>Status: Ditolak oleh Direktur Sekolah (Perlu Penyesuaian)</strong>
                        <p style={{ margin: '2px 0 0', fontSize: '0.8rem', color: '#b91c1c' }}>
                          Catatan Direktur Sekolah: &ldquo;{selectedLevelCfg.fee_approval_note || 'Mohon sesuaikan kembali nominal tarif biaya.'}&rdquo;
                        </p>
                      </div>
                    </div>
                  )}

                  {/* TAB 1: TARIF BIAYA & TERMIN */}
                  {feeModalTab === 'fees' && (
                    <>
                      <div className="adm-form-card-section">
                        <div className="adm-form-section-head">
                          <CreditCard size={15} color="#0284c7" />
                          <span>Standar Rincian Tarif Biaya Masuk &amp; SPP Bulanan</span>
                          <span className="adm-form-section-head-badge">Format Rupiah otomatis saat diketik</span>
                        </div>
                        <div className="adm-grid-3">
                          <div className="adm-field">
                            <label>Registration Fee (Rp)</label>
                            <CurrencyInput
                              value={selectedLevelCfg.registration_fee}
                              onChange={(val) =>
                                setSelectedLevelCfg({ ...selectedLevelCfg, registration_fee: val })
                              }
                            />
                          </div>
                          <div className="adm-field">
                            <label>Test / Reg. Form Fee (Rp)</label>
                            <CurrencyInput
                              value={selectedLevelCfg.test_reg_form_fee}
                              onChange={(val) =>
                                setSelectedLevelCfg({ ...selectedLevelCfg, test_reg_form_fee: val })
                              }
                            />
                          </div>
                          <div className="adm-field">
                            <label>Admission Fee (Rp)</label>
                            <CurrencyInput
                              value={selectedLevelCfg.admission_fee}
                              onChange={(val) =>
                                setSelectedLevelCfg({ ...selectedLevelCfg, admission_fee: val })
                              }
                            />
                          </div>
                          <div className="adm-field">
                            <label>Development Fee / Uang Pangkal (Rp)</label>
                            <CurrencyInput
                              value={selectedLevelCfg.development_fee}
                              onChange={(val) =>
                                setSelectedLevelCfg({ ...selectedLevelCfg, development_fee: val })
                              }
                            />
                          </div>
                          <div className="adm-field">
                            <label>Books &amp; Activity Fee (Rp)</label>
                            <CurrencyInput
                              value={selectedLevelCfg.books_activity_fee}
                              onChange={(val) =>
                                setSelectedLevelCfg({ ...selectedLevelCfg, books_activity_fee: val })
                              }
                            />
                          </div>
                          <div className="adm-field">
                            <label>Tuition Fee / SPP Bulanan (Rp)</label>
                            <CurrencyInput
                              value={selectedLevelCfg.tuition_fee}
                              onChange={(val) =>
                                setSelectedLevelCfg({ ...selectedLevelCfg, tuition_fee: val })
                              }
                            />
                          </div>
                        </div>
                      </div>

                      <div className="adm-form-card-section">
                        <div className="adm-form-section-head">
                          <Sparkles size={15} color="#0284c7" />
                          <span>Rincian Termin Cicilan Uang Pangkal (Development Fee)</span>
                          <span className="adm-form-section-head-badge">Termin 1 + 2 + 3 + 4 wajib seimbang</span>
                        </div>

                        <div className="adm-grid-4">
                          <div className="adm-field">
                            <label>Termin DF 1 (Saat Daftar Ulang)</label>
                            <CurrencyInput
                              value={selectedLevelCfg.df_1_amount}
                              onChange={(val) =>
                                setSelectedLevelCfg({ ...selectedLevelCfg, df_1_amount: val })
                              }
                            />
                          </div>
                          <div className="adm-field">
                            <label>Termin DF 2 (Rp)</label>
                            <CurrencyInput
                              value={selectedLevelCfg.df_2_amount}
                              onChange={(val) =>
                                setSelectedLevelCfg({ ...selectedLevelCfg, df_2_amount: val })
                              }
                            />
                          </div>
                          <div className="adm-field">
                            <label>Termin DF 3 (Rp)</label>
                            <CurrencyInput
                              value={selectedLevelCfg.df_3_amount}
                              onChange={(val) =>
                                setSelectedLevelCfg({ ...selectedLevelCfg, df_3_amount: val })
                              }
                            />
                          </div>
                          <div className="adm-field">
                            <label>Termin DF 4 (Rp)</label>
                            <CurrencyInput
                              value={selectedLevelCfg.df_4_amount}
                              onChange={(val) =>
                                setSelectedLevelCfg({ ...selectedLevelCfg, df_4_amount: val })
                              }
                            />
                          </div>
                        </div>

                        {/* LIVE TERMIN CALCULATOR & QUICK ACTIONS */}
                        <DevelopmentFeeTerminCalculator
                          developmentFee={selectedLevelCfg.development_fee}
                          df1={selectedLevelCfg.df_1_amount}
                          df2={selectedLevelCfg.df_2_amount}
                          df3={selectedLevelCfg.df_3_amount}
                          df4={selectedLevelCfg.df_4_amount}
                          onApplySplit={({ df1, df2, df3, df4 }) => {
                            setSelectedLevelCfg((prev) => ({
                              ...prev,
                              df_1_amount: df1,
                              df_2_amount: df2,
                              df_3_amount: df3,
                              df_4_amount: df4,
                            }));
                          }}
                        />
                      </div>
                    </>
                  )}

                  {/* TAB 2: STATUS & MATERI SELEKSI */}
                  {feeModalTab === 'status_mat' && (
                    <>
                      <div className="adm-form-card-section">
                        <div className="adm-form-section-head">
                          <Settings size={15} color="#0284c7" />
                          <span>Status Operasional Pembukaan Pendaftaran</span>
                        </div>
                        <div className="adm-grid-2">
                          <div className="adm-field">
                            <label>Status Pembukaan Jenjang</label>
                            <select
                              value={selectedLevelCfg.is_open ? '1' : '0'}
                              onChange={(e) =>
                                setSelectedLevelCfg({ ...selectedLevelCfg, is_open: e.target.value === '1' })
                              }
                            >
                              <option value="1">Dibuka (Aktif Menerima Pendaftaran Calon Siswa)</option>
                              <option value="0">Ditutup Sementara / Coming Soon (Misal: SMA)</option>
                            </select>
                          </div>
                          <div className="adm-field">
                            <label>Keterangan Jika Belum Dibuka (Coming Soon Note)</label>
                            <input
                              type="text"
                              value={selectedLevelCfg.coming_soon_note || ''}
                              onChange={(e) =>
                                setSelectedLevelCfg({ ...selectedLevelCfg, coming_soon_note: e.target.value })
                              }
                              placeholder="Contoh: Pembukaan Jenjang SMA TA 2027/2028"
                            />
                          </div>
                        </div>
                      </div>

                      <div className="adm-form-card-section" style={{ background: '#f0f9ff', border: '1px dashed #0284c7', borderRadius: 8, padding: '16px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#0369a1', fontWeight: 600, fontSize: '0.92rem', marginBottom: 6 }}>
                          <BookOpen size={18} />
                          <span>Kurikulum Materi Asesmen &amp; Kartu Tes Dikelola Pada Menu Khusus</span>
                        </div>
                        <p style={{ margin: 0, fontSize: '0.84rem', color: '#475569', lineHeight: 1.55 }}>
                          Pengelolaan materi ujian, perlengkapan wajib peserta, tata tertib seleksi, dan catatan ruang tunggu orang tua kini dikelola secara mandiri melalui menu <strong>Materi Asesmen &amp; Kartu Tes</strong> (dengan alur persetujuan Kepala Sekolah). Formulir ini difokuskan khusus untuk pengaturan tarif biaya resmi dan status pendaftaran.
                        </p>
                      </div>
                    </>
                  )}

                  {/* TAB 3: REKENING & PEJABAT PENANDATANGAN */}
                  {feeModalTab === 'bank_sign' && (
                    <>
                      <div className="adm-form-card-section">
                        <div className="adm-form-section-head">
                          <School size={15} color="#0284c7" />
                          <span>Rekening Bank Resmi Penerima Pembayaran Sekolah</span>
                          <span className="adm-form-section-head-badge">Ditampilkan pada invoice &amp; tata cara transfer</span>
                        </div>
                        <div className="adm-grid-3">
                          <div className="adm-field">
                            <label>Nama Bank Rekening Sekolah</label>
                            <input
                              type="text"
                              value={selectedLevelCfg.bank_name || ''}
                              onChange={(e) => setSelectedLevelCfg({ ...selectedLevelCfg, bank_name: e.target.value })}
                              placeholder="BCA (Bank Central Asia)"
                            />
                          </div>
                          <div className="adm-field">
                            <label>Nomor Rekening Sekolah</label>
                            <input
                              type="text"
                              value={selectedLevelCfg.bank_account_number || ''}
                              onChange={(e) =>
                                setSelectedLevelCfg({ ...selectedLevelCfg, bank_account_number: e.target.value })
                              }
                              placeholder="548-098-7788"
                            />
                          </div>
                          <div className="adm-field">
                            <label>Atas Nama Rekening</label>
                            <input
                              type="text"
                              value={selectedLevelCfg.bank_account_holder || ''}
                              onChange={(e) =>
                                setSelectedLevelCfg({ ...selectedLevelCfg, bank_account_holder: e.target.value })
                              }
                              placeholder="Yayasan Pendidikan Permata Harapan"
                            />
                          </div>
                        </div>
                      </div>

                      <div className="adm-form-card-section">
                        <div className="adm-form-section-head">
                          <ShieldCheck size={15} color="#0284c7" />
                          <span>Pejabat Penandatangan Dokumen Resmi (Signatories Governance)</span>
                        </div>
                        <div className="adm-grid-2">
                          <div className="adm-field">
                            <label>Nama Kepala Sekolah (School Principal)</label>
                            <input
                              type="text"
                              value={selectedLevelCfg.principal_name || ''}
                              placeholder={
                                ['preschool', 'kindergarten'].includes(selectedLevelCfg.level_code)
                                  ? 'Ratna Sari, S.Pd., M.Pd'
                                  : 'Asnah Roidah Yulianti, S.Si., M.Pd'
                              }
                              onChange={(e) =>
                                setSelectedLevelCfg({ ...selectedLevelCfg, principal_name: e.target.value })
                              }
                            />
                            <span className="adm-currency-subhelp" style={{ marginTop: 2 }}>
                              Dicantumkan pada Letter of Acceptance (LoA) &amp; Sertifikat Resmi Siswa.
                            </span>
                          </div>
                          <div className="adm-field">
                            <label>Jabatan Kepala Sekolah (Title)</label>
                            <input
                              type="text"
                              value={selectedLevelCfg.principal_title || 'School Principal'}
                              onChange={(e) =>
                                setSelectedLevelCfg({ ...selectedLevelCfg, principal_title: e.target.value })
                              }
                            />
                          </div>
                        </div>

                        <div className="adm-grid-2" style={{ marginTop: 14 }}>
                          <div className="adm-field">
                            <label>Nama PIC Keuangan (Finance PIC)</label>
                            <input
                              type="text"
                              value={selectedLevelCfg.finance_pic_name || ''}
                              placeholder="Zahra, S.E."
                              onChange={(e) =>
                                setSelectedLevelCfg({ ...selectedLevelCfg, finance_pic_name: e.target.value })
                              }
                            />
                            <span className="adm-currency-subhelp" style={{ marginTop: 2 }}>
                              Dicantumkan pada Kwitansi Resmi (Official Payment Receipt).
                            </span>
                          </div>
                          <div className="adm-field">
                            <label>Jabatan Keuangan (Title)</label>
                            <input
                              type="text"
                              value={selectedLevelCfg.finance_pic_title || 'Finance & Treasury PIC'}
                              onChange={(e) =>
                                setSelectedLevelCfg({ ...selectedLevelCfg, finance_pic_title: e.target.value })
                              }
                            />
                          </div>
                        </div>
                      </div>
                    </>
                  )}

                </div>
                <div className="adm-config-modal-footer">
                  <button
                    type="button"
                    className="adm-btn-outline"
                    onClick={() => setIsFeeModalOpen(false)}
                  >
                    Batal
                  </button>
                  <button type="submit" className="adm-btn-primary" disabled={savingLevelCfg}>
                    {savingLevelCfg ? 'Menyimpan...' : 'Simpan & Ajukan Perubahan Biaya ke Direktur Sekolah'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* ===================================================================
            MODAL 4: CREATE PROMO / DISCOUNT CODE
        =================================================================== */}
        {isPromoModalOpen && (
          <div className="adm-config-modal-overlay" onClick={() => setIsPromoModalOpen(false)}>
            <div className="adm-config-modal-box" style={{ maxWidth: 740 }} onClick={(e) => e.stopPropagation()}>
              <div className="adm-config-modal-head">
                <div className="adm-config-modal-head-title">
                  <div className="adm-config-modal-head-icon">
                    <Tag size={18} />
                  </div>
                  <div>
                    <h3>Buat Kode Voucher Diskon Baru</h3>
                    <p style={{ margin: 0, fontSize: '0.78rem', color: '#94a3b8' }}>
                      Rilis kupon diskon uang pangkal untuk program Open House atau promo khusus.
                    </p>
                  </div>
                </div>
                <button type="button" onClick={() => setIsPromoModalOpen(false)} title="Tutup Modal">
                  <X size={18} />
                </button>
              </div>
              <form onSubmit={handleCreatePromo} style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
                <div className="adm-config-modal-body">
                  <div className="adm-form-card-section">
                    <div className="adm-form-section-head">
                      <Sparkles size={15} color="#0284c7" />
                      <span>Identitas &amp; Nama Program Voucher</span>
                    </div>
                    <div className="adm-grid-2">
                      <div className="adm-field">
                        <label>Kode Promo (Kapital &amp; Tanpa Spasi) *</label>
                        <input
                          type="text"
                          value={promoForm.promo_code}
                          onChange={(e) =>
                            setPromoForm({ ...promoForm, promo_code: e.target.value.toUpperCase().replace(/\s/g, '') })
                          }
                          placeholder="Contoh: OPENHOUSE2026"
                          required
                          style={{ letterSpacing: '0.05em', fontWeight: 700 }}
                        />
                      </div>
                      <div className="adm-field">
                        <label>Nama Program Promo *</label>
                        <input
                          type="text"
                          value={promoForm.promo_title}
                          onChange={(e) => setPromoForm({ ...promoForm, promo_title: e.target.value })}
                          placeholder="Contoh: Voucher Open House Campus 1 (Pos Pengumben)"
                          required
                        />
                      </div>
                    </div>
                  </div>

                  <div className="adm-form-card-section">
                    <div className="adm-form-section-head">
                      <CreditCard size={15} color="#0284c7" />
                      <span>Skema Potongan &amp; Target Jenjang</span>
                    </div>
                    <div className="adm-grid-3">
                      <div className="adm-field">
                        <label>Tipe Potongan</label>
                        <select
                          value={promoForm.discount_type}
                          onChange={(e) => setPromoForm({ ...promoForm, discount_type: e.target.value })}
                        >
                          <option value="fixed">Nominal Tetap (Rp)</option>
                          <option value="percentage">Persentase (%) dari Uang Pangkal</option>
                        </select>
                      </div>

                      <div className="adm-field">
                        <label>
                          Nilai Potongan ({promoForm.discount_type === 'percentage' ? '%' : 'Rp'}) *
                        </label>
                        {promoForm.discount_type === 'percentage' ? (
                          <div className="adm-percentage-input-wrap">
                            <input
                              type="number"
                              min="1"
                              max="100"
                              value={promoForm.discount_value}
                              onChange={(e) => setPromoForm({ ...promoForm, discount_value: e.target.value })}
                              placeholder="Contoh: 10"
                              required
                            />
                            <span className="adm-input-suffix">%</span>
                          </div>
                        ) : (
                          <CurrencyInput
                            value={promoForm.discount_value}
                            onChange={(val) => setPromoForm({ ...promoForm, discount_value: val })}
                            placeholder="Contoh: 2.000.000"
                            required
                          />
                        )}
                      </div>

                      <div className="adm-field">
                        <label>Target Jenjang</label>
                        <select
                          value={promoForm.applies_to_levels}
                          onChange={(e) => setPromoForm({ ...promoForm, applies_to_levels: e.target.value })}
                        >
                          <option value="all">Semua Jenjang</option>
                          <option value="preschool">Preschool</option>
                          <option value="kindergarten">Kindergarten</option>
                          <option value="primary">Primary</option>
                          <option value="secondary">Secondary</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  <div className="adm-form-card-section">
                    <div className="adm-form-section-head">
                      <Calendar size={15} color="#0284c7" />
                      <span>Kapasitas Kuota &amp; Batas Masa Berlaku</span>
                    </div>
                    <div className="adm-grid-2">
                      <div className="adm-field">
                        <label>Kapasitas Kuota Penggunaan Voucher *</label>
                        <QuantityStepperInput
                          value={promoForm.max_quota || 20}
                          onChange={(val) => setPromoForm({ ...promoForm, max_quota: val })}
                          unit="Voucher"
                          min={1}
                          quickAdds={[5, 10]}
                        />
                      </div>
                      <div className="adm-field">
                        <label>Masa Berlaku Sampai Tanggal *</label>
                        <input
                          type="date"
                          value={promoForm.valid_until}
                          onChange={(e) => setPromoForm({ ...promoForm, valid_until: e.target.value })}
                          required
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="adm-config-modal-footer">
                  <button
                    type="button"
                    className="adm-btn-outline"
                    onClick={() => setIsPromoModalOpen(false)}
                    disabled={isSavingPromo}
                  >
                    Batal
                  </button>
                  <button type="submit" className="adm-btn-primary" disabled={isSavingPromo}>
                    {isSavingPromo ? 'Mengajukan...' : 'Ajukan & Simpan Kode Promo'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* ===================================================================
            MODAL 5: PROMO CODE USAGE HISTORY & AUDIT LIST
        =================================================================== */}
        {promoHistoryModal && (
          <div className="adm-config-modal-overlay" onClick={() => setPromoHistoryModal(null)}>
            <div className="adm-config-modal-box" style={{ maxWidth: 860 }} onClick={(e) => e.stopPropagation()}>
              <div className="adm-config-modal-head">
                <div className="adm-config-modal-head-title">
                  <div className="adm-config-modal-head-icon">
                    <Tag size={18} />
                  </div>
                  <div>
                    <h3>Riwayat Penggunaan Kode Voucher Promo</h3>
                    <div style={{ fontSize: '0.78rem', color: '#94a3b8', marginTop: 2 }}>
                      Kode: <strong className="adm-mono" style={{ color: '#38bdf8' }}>{promoHistoryModal.promo?.promo_code}</strong> &bull; {promoHistoryModal.promo?.promo_title}
                    </div>
                  </div>
                </div>
                <button type="button" onClick={() => setPromoHistoryModal(null)} title="Tutup Modal">
                  <X size={18} />
                </button>
              </div>

              <div className="adm-config-modal-body">
                {loadingPromoUsers ? (
                  <div style={{ textAlign: 'center', padding: '36px 0', color: '#64748b' }}>
                    <RefreshCw size={24} className="adm-spin" style={{ margin: '0 auto 8px', display: 'block' }} />
                    Memuat daftar pendaftar yang menggunakan voucher ini...
                  </div>
                ) : (promoHistoryModal.users || []).length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '36px 16px', color: '#64748b' }}>
                    <Tag size={36} color="#94a3b8" style={{ margin: '0 auto 10px', display: 'block' }} />
                    <strong style={{ display: 'block', fontSize: 15, color: '#1e293b' }}>Belum Ada Pengguna Voucher</strong>
                    <p style={{ margin: '6px 0 0', fontSize: 13 }}>
                      Kode voucher ini masih utuh ({promoHistoryModal.promo?.max_quota || 0} kuota) dan belum pernah digunakan oleh pendaftar manapun.
                    </p>
                  </div>
                ) : (
                  <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
                      <span style={{ fontSize: 13, color: '#334155' }}>
                        Total Pengguna: <strong>{promoHistoryModal.users.length}</strong> / <strong>{promoHistoryModal.promo?.max_quota || 0}</strong> kuota terpakai
                      </span>
                      <span className="adm-status-locked-chip">
                        <Lock size={12} /> Voucher Memiliki History Pengguna (Terkunci dari Penghapusan)
                      </span>
                    </div>

                    <div className="adm-data-table-wrap">
                      <table className="adm-data-table">
                        <thead>
                          <tr>
                            <th>No. Registrasi</th>
                            <th>Nama Calon Siswa</th>
                            <th>Jenjang &amp; Grade</th>
                            <th>Nama Orang Tua</th>
                            <th>Kontak WhatsApp</th>
                            <th>Waktu Pendaftaran</th>
                            <th>Potongan Diskon</th>
                            <th>Status Pendaftar</th>
                          </tr>
                        </thead>
                        <tbody>
                          {promoHistoryModal.users.map((u) => (
                            <tr key={u.application_id}>
                              <td>
                                <strong className="adm-mono" style={{ color: '#0f172a' }}>{u.registration_no}</strong>
                              </td>
                              <td>
                                <strong>{u.child_full_name}</strong>
                              </td>
                              <td>
                                <span>{u.level_name} ({u.grade_target})</span>
                              </td>
                              <td>{u.parent_name}</td>
                              <td>
                                {u.parent_phone ? (
                                  <a
                                    href={`https://wa.me/${u.parent_phone.replace(/[^\d]/g, '')}`}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="adm-file-link"
                                  >
                                    {u.parent_phone}
                                  </a>
                                ) : '-'}
                              </td>
                              <td style={{ fontSize: 12 }}>
                                {formatDateId(u.applied_at)}
                              </td>
                              <td>
                                <strong className="adm-mono" style={{ color: '#059669' }}>
                                  {formatCurrency(u.discount_amount)}
                                </strong>
                              </td>
                              <td>
                                <span className="adm-badge adm-badge-blue" style={{ fontSize: 11, padding: '3px 7px' }}>
                                  {u.status_label || u.status_code}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>

              <div className="adm-config-modal-footer">
                <button
                  type="button"
                  className="adm-btn-outline"
                  onClick={() => setPromoHistoryModal(null)}
                >
                  Tutup Riwayat
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ===================================================================
            DETAIL MODAL 1: PERIODE PENDAFTARAN & ALOKASI KUOTA
        =================================================================== */}
        {detailPeriodModal && (
          <div className="adm-config-modal-overlay" onClick={() => setDetailPeriodModal(null)}>
            <div className="adm-config-modal-box" style={{ maxWidth: 740 }} onClick={(e) => e.stopPropagation()}>
              <div className="adm-config-modal-head">
                <div className="adm-config-modal-head-title">
                  <div className="adm-config-modal-head-icon">
                    <Calendar size={18} />
                  </div>
                  <div>
                    <h3>Detail Periode Pendaftaran: {detailPeriodModal.period_name}</h3>
                    <p style={{ margin: 0, fontSize: '0.78rem', color: '#94a3b8' }}>
                      Tahun Ajaran {detailPeriodModal.academic_year} &bull; {formatDateId(detailPeriodModal.start_date)} s/d {formatDateId(detailPeriodModal.end_date)}
                    </p>
                  </div>
                </div>
                <button type="button" onClick={() => setDetailPeriodModal(null)} title="Tutup Modal">
                  <X size={18} />
                </button>
              </div>

              <div className="adm-config-modal-body">
                {detailPeriodModal.is_pending_approval ? (
                  <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: '12px 16px', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 12 }}>
                    <Clock size={20} color="#b45309" />
                    <div style={{ fontSize: 13, color: '#92400e' }}>
                      <strong>Status: Menunggu Persetujuan Direktur Sekolah</strong>
                      <div>Pengajuan gelombang periode ini telah dikirim ke Direktur Sekolah (School Director). Belum aktif dibuka untuk umum sampai disetujui.</div>
                    </div>
                  </div>
                ) : detailPeriodModal.is_active ? (
                  <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, padding: '12px 16px', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 12 }}>
                    <CheckCircle2 size={20} color="#16a34a" />
                    <div style={{ fontSize: 13, color: '#166534' }}>
                      <strong>Status: Gelombang Resmi &amp; Aktif Menerima Pendaftaran</strong>
                      <div>Orang tua calon siswa dapat memilih periode ini pada saat pendaftaran formulir baru.</div>
                    </div>
                  </div>
                ) : (
                  <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '12px 16px', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 12 }}>
                    <Info size={20} color="#64748b" />
                    <div style={{ fontSize: 13, color: '#475569' }}>
                      <strong>Status: Periode Nonaktif / Ditutup</strong>
                      <div>Periode ini sedang tidak menerima pendaftaran baru.</div>
                    </div>
                  </div>
                )}

                <div className="adm-info-grid" style={{ marginBottom: 16 }}>
                  <div className="adm-info-item">
                    <label>Nama Gelombang</label>
                    <strong>{detailPeriodModal.period_name}</strong>
                  </div>
                  <div className="adm-info-item">
                    <label>Tahun Ajaran</label>
                    <strong className="adm-mono">{detailPeriodModal.academic_year}</strong>
                  </div>
                  <div className="adm-info-item">
                    <label>Tanggal Buka</label>
                    <strong>{formatDateId(detailPeriodModal.start_date)}</strong>
                  </div>
                  <div className="adm-info-item">
                    <label>Tanggal Tutup</label>
                    <strong>{formatDateId(detailPeriodModal.end_date)}</strong>
                  </div>
                  <div className="adm-info-item" style={{ gridColumn: 'span 2' }}>
                    <label>Catatan Internal Gelombang</label>
                    <span>{detailPeriodModal.notes || '-'}</span>
                  </div>
                </div>

                <div style={{ marginTop: 14 }}>
                  <h4 style={{ margin: '0 0 10px', fontSize: 14, color: '#1e293b' }}>Alokasi Kuota Kursi Tiap Jenjang</h4>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 10 }}>
                    <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '10px 14px', textAlign: 'center' }}>
                      <div style={{ fontSize: 11, color: '#64748b', fontWeight: 600 }}>PRESCHOOL</div>
                      <div style={{ fontSize: 18, fontWeight: 700, color: '#0369a1', marginTop: 4 }}>{detailPeriodModal.quotas?.preschool ?? 0} <span style={{ fontSize: 12, fontWeight: 400 }}>Kursi</span></div>
                    </div>
                    <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '10px 14px', textAlign: 'center' }}>
                      <div style={{ fontSize: 11, color: '#64748b', fontWeight: 600 }}>KINDERGARTEN</div>
                      <div style={{ fontSize: 18, fontWeight: 700, color: '#b45309', marginTop: 4 }}>{detailPeriodModal.quotas?.kindergarten ?? 0} <span style={{ fontSize: 12, fontWeight: 400 }}>Kursi</span></div>
                    </div>
                    <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '10px 14px', textAlign: 'center' }}>
                      <div style={{ fontSize: 11, color: '#64748b', fontWeight: 600 }}>PRIMARY (SD)</div>
                      <div style={{ fontSize: 18, fontWeight: 700, color: '#6b21a8', marginTop: 4 }}>{detailPeriodModal.quotas?.primary ?? 0} <span style={{ fontSize: 12, fontWeight: 400 }}>Kursi</span></div>
                    </div>
                    <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '10px 14px', textAlign: 'center' }}>
                      <div style={{ fontSize: 11, color: '#64748b', fontWeight: 600 }}>SECONDARY (SMP)</div>
                      <div style={{ fontSize: 18, fontWeight: 700, color: '#047857', marginTop: 4 }}>{detailPeriodModal.quotas?.secondary ?? 0} <span style={{ fontSize: 12, fontWeight: 400 }}>Kursi</span></div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="adm-config-modal-footer" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  {!detailPeriodModal.is_pending_approval && (
                    <button
                      type="button"
                      className="adm-btn-action-edit"
                      onClick={() => {
                        const p = detailPeriodModal;
                        setDetailPeriodModal(null);
                        setEditingPeriod(p);
                        setPeriodForm({
                          period_name: p.period_name,
                          academic_year: p.academic_year,
                          start_date: p.start_date,
                          end_date: p.end_date,
                          quotas: p.quotas || { preschool: 25, kindergarten: 30, primary: 60, secondary: 45, sma: 0 },
                          is_active: p.is_active,
                          next_period_id: p.next_period_id || '',
                          notes: p.notes || '',
                        });
                        setIsPeriodModalOpen(true);
                      }}
                    >
                      <Edit3 size={14} />
                      <span>Edit Periode</span>
                    </button>
                  )}
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button
                    type="button"
                    className="adm-btn-action-del"
                    onClick={() => {
                      const id = detailPeriodModal.id;
                      setDetailPeriodModal(null);
                      handleDeletePeriod(id);
                    }}
                  >
                    <Trash2 size={14} />
                    <span>{detailPeriodModal.is_pending_approval ? 'Batalkan Pengajuan' : 'Hapus Periode'}</span>
                  </button>
                  <button type="button" className="adm-btn-outline" onClick={() => setDetailPeriodModal(null)}>
                    Tutup
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ===================================================================
            DETAIL MODAL 2: JADWAL SESI TES & OBSERVASI
        =================================================================== */}
        {detailSlotModal && (() => {
          const slotAttendees = (applications || []).filter(
            (app) => Number(app.test_slot_id) === Number(detailSlotModal.id)
          );
          const isFull = Number(detailSlotModal.booked_count || 0) >= Number(detailSlotModal.slot_quota || 0);

          return (
            <div className="adm-config-modal-overlay" onClick={() => setDetailSlotModal(null)}>
              <div className="adm-config-modal-box" style={{ maxWidth: 880 }} onClick={(e) => e.stopPropagation()}>
                <div className="adm-config-modal-head">
                  <div className="adm-config-modal-head-title">
                    <div className="adm-config-modal-head-icon">
                      <Calendar size={18} />
                    </div>
                    <div>
                      <h3>Detail Jadwal Sesi Tes: {detailSlotModal.session_name}</h3>
                      <p style={{ margin: 0, fontSize: '0.78rem', color: '#94a3b8' }}>
                        {formatDateId(detailSlotModal.test_date)} &bull; {detailSlotModal.test_time} &bull; {detailSlotModal.campus_location?.split(' - ')[0]}
                      </p>
                    </div>
                  </div>
                  <button type="button" onClick={() => setDetailSlotModal(null)} title="Tutup Modal">
                    <X size={18} />
                  </button>
                </div>

                <div className="adm-config-modal-body">
                  {detailSlotModal.is_pending_approval ? (
                    <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: '12px 16px', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 12 }}>
                      <Clock size={20} color="#b45309" />
                      <div style={{ fontSize: 13, color: '#92400e' }}>
                        <strong>Status: Menunggu Persetujuan Principal</strong>
                        <div>Pengajuan jadwal sesi tes ini telah dikirim ke Kepala Sekolah (Principal). Belum dapat digunakan untuk penempatan peserta sampai disetujui.</div>
                      </div>
                    </div>
                  ) : isFull ? (
                    <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '12px 16px', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 12 }}>
                      <Lock size={20} color="#dc2626" />
                      <div style={{ fontSize: 13, color: '#991b1b' }}>
                        <strong>Status: Kapasitas Sesi Penuh ({detailSlotModal.booked_count} / {detailSlotModal.slot_quota} Peserta)</strong>
                        <div>Kuota ruangan telah terisi maksimal. Admin tidak dapat menambahkan peserta baru ke sesi ini kecuali kuota dinaikkan.</div>
                      </div>
                    </div>
                  ) : (
                    <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, padding: '12px 16px', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 12 }}>
                      <CheckCircle2 size={20} color="#16a34a" />
                      <div style={{ fontSize: 13, color: '#166534' }}>
                        <strong>Status: Jadwal Tes Resmi &amp; Siap Digunakan</strong>
                        <div>Sesi ini dapat dipilih saat validasi berkas pendaftar. Sisa kuota: {Math.max(0, Number(detailSlotModal.slot_quota || 0) - Number(detailSlotModal.booked_count || 0))} kursi.</div>
                      </div>
                    </div>
                  )}

                  <div className="adm-info-grid" style={{ marginBottom: 16 }}>
                    <div className="adm-info-item">
                      <label>Nama Sesi Tes</label>
                      <strong>{detailSlotModal.session_name}</strong>
                    </div>
                    <div className="adm-info-item">
                      <label>Target Jenjang</label>
                      <strong className="adm-mono">{detailSlotModal.level_code === 'all' ? 'Semua Jenjang' : detailSlotModal.level_code.toUpperCase()}</strong>
                    </div>
                    <div className="adm-info-item">
                      <label>Tanggal &amp; Waktu Tes</label>
                      <strong>{formatDateId(detailSlotModal.test_date)}, {detailSlotModal.test_time}</strong>
                    </div>
                    <div className="adm-info-item">
                      <label>Kapasitas Peserta Ruangan</label>
                      <strong style={{ color: isFull ? '#dc2626' : '#0369a1' }}>
                        {detailSlotModal.booked_count} / {detailSlotModal.slot_quota} Kursi Terisi
                      </strong>
                    </div>
                    <div className="adm-info-item" style={{ gridColumn: 'span 2' }}>
                      <label>Lokasi Kampus &amp; Ruangan Assessment</label>
                      <span>{detailSlotModal.campus_location} &mdash; <strong>{detailSlotModal.room_name || '-'}</strong></span>
                    </div>
                    {detailSlotModal.custom_notes && (
                      <div className="adm-info-item" style={{ gridColumn: 'span 2' }}>
                        <label>Catatan Khusus / Perlengkapan Siswa</label>
                        <span>{detailSlotModal.custom_notes}</span>
                      </div>
                    )}
                  </div>

                  <div style={{ marginTop: 16 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                      <h4 style={{ margin: 0, fontSize: 14, color: '#1e293b' }}>
                        Daftar Calon Siswa Terjadwal pada Sesi Ini ({slotAttendees.length})
                      </h4>
                      {slotAttendees.length > 0 && (
                        <button
                          type="button"
                          className="adm-btn-action-presensi"
                          onClick={() => {
                            const sl = detailSlotModal;
                            setDetailSlotModal(null);
                            handlePrintAttendanceSheet(sl, slotAttendees);
                          }}
                        >
                          <Printer size={13} />
                          <span>Cetak Presensi</span>
                        </button>
                      )}
                    </div>

                    {slotAttendees.length === 0 ? (
                      <div style={{ textAlign: 'center', padding: '28px 16px', background: '#f8fafc', borderRadius: 8, border: '1px dashed #cbd5e1', color: '#64748b' }}>
                        <Users size={32} color="#94a3b8" style={{ margin: '0 auto 8px', display: 'block' }} />
                        <strong style={{ display: 'block', fontSize: 14, color: '#1e293b' }}>Belum Ada Peserta Terjadwal</strong>
                        <p style={{ margin: '4px 0 0', fontSize: 12.5 }}>
                          Peserta tes dialokasikan oleh Admin Admissions pada formulir validasi berkas pendaftar.
                        </p>
                      </div>
                    ) : (
                      <div className="adm-data-table-wrap">
                        <table className="adm-data-table">
                          <thead>
                            <tr>
                              <th>No. Registrasi</th>
                              <th>Nama Calon Siswa</th>
                              <th>Jenjang</th>
                              <th>Orang Tua / Kontak</th>
                              <th>Status Seleksi</th>
                            </tr>
                          </thead>
                          <tbody>
                            {slotAttendees.map((att) => (
                              <tr key={att.id}>
                                <td><strong className="adm-mono">{att.registration_number || '-'}</strong></td>
                                <td><strong>{att.child_full_name}</strong></td>
                                <td><span className="adm-mono">{(att.level_name || att.level_code || '').toUpperCase()}</span></td>
                                <td>
                                  <div>{att.parent_name || att.father_name || att.mother_name || '-'}</div>
                                  <div style={{ fontSize: 11, color: '#64748b' }}>{att.parent_phone || att.whatsapp_number || '-'}</div>
                                </td>
                                <td>
                                  <span className="adm-badge adm-badge-blue" style={{ fontSize: 11, padding: '3px 7px' }}>
                                    {att.status_label || att.status_code}
                                  </span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </div>

                <div className="adm-config-modal-footer" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button
                      type="button"
                      className="adm-btn-action-presensi"
                      onClick={() => {
                        const sl = detailSlotModal;
                        setDetailSlotModal(null);
                        setAttendanceModalSlot(sl);
                      }}
                      title="Lihat Lembar Presensi Lengkap & Cetak Format A4"
                    >
                      <Users size={14} />
                      <span>Lembar Presensi ({slotAttendees.length})</span>
                    </button>
                    {!detailSlotModal.is_pending_approval && (
                      <button
                        type="button"
                        className="adm-btn-action-edit"
                        onClick={() => {
                          const sl = detailSlotModal;
                          setDetailSlotModal(null);
                          setEditingSlot(sl);
                          setSlotForm({
                            period_id: sl.period_id || overview.periods?.[0]?.id || 1,
                            level_code: sl.level_code || 'all',
                            session_name: sl.session_name,
                            test_date: sl.test_date,
                            test_time: sl.test_time,
                            campus_location: sl.campus_location,
                            room_name: sl.room_name,
                            slot_quota: sl.slot_quota,
                            custom_notes: sl.custom_notes || '',
                          });
                          setIsSlotModalOpen(true);
                        }}
                      >
                        <Edit3 size={14} />
                        <span>Edit Sesi</span>
                      </button>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    {Number(detailSlotModal.booked_count || 0) > 0 ? (
                      <button
                        type="button"
                        className="adm-btn-action-del is-disabled"
                        disabled
                        title={`Tidak dapat dihapus: telah ada ${detailSlotModal.booked_count} calon siswa terjadwal pada sesi ini`}
                      >
                        <Lock size={14} />
                        <span>Terkunci</span>
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="adm-btn-action-del"
                        onClick={() => {
                          const id = detailSlotModal.id;
                          setDetailSlotModal(null);
                          handleDeleteSlot(id);
                        }}
                      >
                        <Trash2 size={14} />
                        <span>{detailSlotModal.is_pending_approval ? 'Batalkan Pengajuan' : 'Hapus Sesi'}</span>
                      </button>
                    )}
                    <button type="button" className="adm-btn-outline" onClick={() => setDetailSlotModal(null)}>
                      Tutup
                    </button>
                  </div>
                </div>
              </div>
            </div>
          );
        })()}

        {/* ===================================================================
            DETAIL MODAL 3: KODE PROMO & VOUCHER DISKON
        =================================================================== */}
        {detailPromoModal && (
          <div className="adm-config-modal-overlay" onClick={() => setDetailPromoModal(null)}>
            <div className="adm-config-modal-box" style={{ maxWidth: 860 }} onClick={(e) => e.stopPropagation()}>
              <div className="adm-config-modal-head">
                <div className="adm-config-modal-head-title">
                  <div className="adm-config-modal-head-icon">
                    <Tag size={18} />
                  </div>
                  <div>
                    <h3>Detail Voucher Diskon: {detailPromoModal.promo_code}</h3>
                    <p style={{ margin: 0, fontSize: '0.78rem', color: '#94a3b8' }}>
                      {detailPromoModal.promo_title} &bull; Kuota: {detailPromoModal.used_count || 0} / {detailPromoModal.max_quota} Kupon
                    </p>
                  </div>
                </div>
                <button type="button" onClick={() => setDetailPromoModal(null)} title="Tutup Modal">
                  <X size={18} />
                </button>
              </div>

              <div className="adm-config-modal-body">
                {detailPromoModal.is_pending_approval ? (
                  <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: '12px 16px', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 12 }}>
                    <Clock size={20} color="#b45309" />
                    <div style={{ fontSize: 13, color: '#92400e' }}>
                      <strong>Status: Menunggu Persetujuan Direktur Sekolah</strong>
                      <div>Pengajuan kode voucher promo ini telah dikirim ke Direktur Sekolah (School Director). Belum dapat digunakan oleh calon siswa sampai disetujui.</div>
                    </div>
                  </div>
                ) : (
                  <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, padding: '12px 16px', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 12 }}>
                    <CheckCircle2 size={20} color="#16a34a" />
                    <div style={{ fontSize: 13, color: '#166534' }}>
                      <strong>Status: Kode Voucher Resmi &amp; Aktif</strong>
                      <div>Pendaftar dapat memasukkan kode ini pada halaman formulir pembayaran untuk mendapatkan potongan biaya resmi.</div>
                    </div>
                  </div>
                )}

                <div className="adm-info-grid" style={{ marginBottom: 16 }}>
                  <div className="adm-info-item">
                    <label>Kode Voucher</label>
                    <strong className="adm-mono" style={{ color: '#2563eb', fontSize: 15 }}>{detailPromoModal.promo_code}</strong>
                  </div>
                  <div className="adm-info-item">
                    <label>Nama Program Promo</label>
                    <strong>{detailPromoModal.promo_title}</strong>
                  </div>
                  <div className="adm-info-item">
                    <label>Tipe &amp; Besaran Potongan</label>
                    <strong className="adm-mono" style={{ color: '#059669', fontSize: 15 }}>
                      {detailPromoModal.discount_type === 'percentage'
                        ? `${detailPromoModal.discount_value}% dari DF`
                        : formatCurrency(detailPromoModal.discount_value)}
                    </strong>
                  </div>
                  <div className="adm-info-item">
                    <label>Berlaku Untuk Jenjang</label>
                    <strong className="adm-mono">
                      {detailPromoModal.applies_to_levels === 'all' ? 'Semua Jenjang' : detailPromoModal.applies_to_levels?.toUpperCase()}
                    </strong>
                  </div>
                  <div className="adm-info-item">
                    <label>Kapasitas Kuota Penggunaan</label>
                    <strong>{detailPromoModal.used_count || 0} / {detailPromoModal.max_quota} Kupon Terpakai</strong>
                  </div>
                  <div className="adm-info-item">
                    <label>Masa Berlaku Voucher</label>
                    <strong>s/d {formatDateId(detailPromoModal.valid_until)}</strong>
                  </div>
                </div>

                <div style={{ marginTop: 16 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                    <h4 style={{ margin: 0, fontSize: 14, color: '#1e293b' }}>
                      Daftar Calon Siswa yang Menggunakan Kode Ini ({detailPromoUsers.length})
                    </h4>
                    {detailPromoUsers.length > 0 && (
                      <span className="adm-status-locked-chip">
                        <Lock size={12} /> Voucher Memiliki History Pengguna
                      </span>
                    )}
                  </div>

                  {loadingDetailPromoUsers ? (
                    <div style={{ textAlign: 'center', padding: '28px 0', color: '#64748b' }}>
                      <RefreshCw size={22} className="adm-spin" style={{ margin: '0 auto 8px', display: 'block' }} />
                      Memuat data pengguna voucher...
                    </div>
                  ) : detailPromoModal.is_pending_approval ? (
                    <div style={{ textAlign: 'center', padding: '24px 16px', background: '#fffbeb', borderRadius: 8, border: '1px dashed #fde68a', color: '#92400e' }}>
                      <Clock size={28} color="#b45309" style={{ margin: '0 auto 8px', display: 'block' }} />
                      <strong style={{ display: 'block', fontSize: 14 }}>Voucher Masih Menunggu Persetujuan</strong>
                      <p style={{ margin: '4px 0 0', fontSize: 12.5 }}>
                        Belum ada calon siswa yang menggunakan kode promo ini karena voucher belum berstatus aktif.
                      </p>
                    </div>
                  ) : detailPromoUsers.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '28px 16px', background: '#f8fafc', borderRadius: 8, border: '1px dashed #cbd5e1', color: '#64748b' }}>
                      <Tag size={32} color="#94a3b8" style={{ margin: '0 auto 8px', display: 'block' }} />
                      <strong style={{ display: 'block', fontSize: 14, color: '#1e293b' }}>Belum Ada Pengguna Voucher</strong>
                      <p style={{ margin: '4px 0 0', fontSize: 12.5 }}>
                        Kode voucher ini masih utuh ({detailPromoModal.max_quota} kuota) dan belum pernah digunakan oleh pendaftar manapun.
                      </p>
                    </div>
                  ) : (
                    <div className="adm-data-table-wrap">
                      <table className="adm-data-table">
                        <thead>
                          <tr>
                            <th>No. Registrasi</th>
                            <th>Nama Calon Siswa</th>
                            <th>Jenjang</th>
                            <th>Nama Orang Tua</th>
                            <th>Kontak WhatsApp</th>
                            <th>Potongan Diskon</th>
                            <th>Status Pendaftar</th>
                          </tr>
                        </thead>
                        <tbody>
                          {detailPromoUsers.map((u) => (
                            <tr key={u.id || u.application_id}>
                              <td><strong className="adm-mono">{u.registration_no}</strong></td>
                              <td><strong>{u.child_full_name}</strong></td>
                              <td><span className="adm-mono">{u.level_name}</span></td>
                              <td>{u.parent_name}</td>
                              <td>
                                {u.parent_phone ? (
                                  <a href={`https://wa.me/${u.parent_phone.replace(/[^\d]/g, '')}`} target="_blank" rel="noreferrer" className="adm-file-link">
                                    {u.parent_phone}
                                  </a>
                                ) : '-'}
                              </td>
                              <td>
                                <strong className="adm-mono" style={{ color: '#059669' }}>
                                  {formatCurrency(u.discount_amount)}
                                </strong>
                              </td>
                              <td>
                                <span className="adm-badge adm-badge-blue" style={{ fontSize: 11, padding: '3px 7px' }}>
                                  {u.status_label || u.status_code}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>

              <div className="adm-config-modal-footer" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div></div>
                <div style={{ display: 'flex', gap: 8 }}>
                  {Number(detailPromoModal.used_count || 0) > 0 ? (
                    <button
                      type="button"
                      className="adm-btn-action-del is-disabled"
                      disabled
                      title={`Tidak dapat dihapus: telah digunakan oleh ${detailPromoModal.used_count} pendaftar`}
                    >
                      <Lock size={14} />
                      <span>Terkunci ({detailPromoModal.used_count} Pengguna)</span>
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="adm-btn-action-del"
                      onClick={() => {
                        const id = detailPromoModal.id;
                        setDetailPromoModal(null);
                        handleDeletePromo(id);
                      }}
                    >
                      <Trash2 size={14} />
                      <span>{detailPromoModal.is_pending_approval ? 'Batalkan Pengajuan' : 'Hapus Kode Promo'}</span>
                    </button>
                  )}
                  <button type="button" className="adm-btn-outline" onClick={() => setDetailPromoModal(null)}>
                    Tutup
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ===================================================================
            MODAL 6: TEST SESSION ATTENDANCE ROSTER & PRINTABLE SHEET
        =================================================================== */}
        {attendanceModalSlot && (() => {
          const scheduledAttendees = (applications || []).filter(
            (app) => Number(app.test_slot_id) === Number(attendanceModalSlot.id)
          );
          return (
            <div className="adm-config-modal-overlay" onClick={() => setAttendanceModalSlot(null)}>
              <div className="adm-config-modal-box" style={{ maxWidth: 880 }} onClick={(e) => e.stopPropagation()}>
                <div className="adm-config-modal-head">
                  <div className="adm-config-modal-head-title">
                    <div className="adm-config-modal-head-icon">
                      <Users size={18} />
                    </div>
                    <div>
                      <h3>Daftar Hadir &amp; Presensi Sesi: {attendanceModalSlot.session_name}</h3>
                      <p style={{ margin: 0, fontSize: '0.78rem', color: '#94a3b8' }}>
                        {formatDateId(attendanceModalSlot.test_date)} &bull; {attendanceModalSlot.test_time} &bull; {attendanceModalSlot.room_name} ({attendanceModalSlot.campus_location.split(' - ')[0]})
                      </p>
                    </div>
                  </div>
                  <button type="button" onClick={() => setAttendanceModalSlot(null)} title="Tutup Modal">
                    <X size={18} />
                  </button>
                </div>

                <div className="adm-config-modal-body">
                  <div className="adm-attendance-summary-bar">
                    <div className="adm-att-info-item">
                      <span className="att-lbl">Ruangan &amp; Kampus</span>
                      <strong className="att-val">{attendanceModalSlot.room_name || '-'} ({attendanceModalSlot.campus_location.split(' - ')[0]})</strong>
                    </div>
                    <div className="adm-att-info-item">
                      <span className="att-lbl">Waktu Pelaksanaan</span>
                      <strong className="att-val">{formatDateId(attendanceModalSlot.test_date)}, {attendanceModalSlot.test_time}</strong>
                    </div>
                    <div className="adm-att-info-item">
                      <span className="att-lbl">Kapasitas Terisi</span>
                      <strong className="att-val" style={{ color: scheduledAttendees.length >= attendanceModalSlot.slot_quota ? '#dc2626' : '#0284c7' }}>
                        {scheduledAttendees.length} / {attendanceModalSlot.slot_quota} Peserta
                      </strong>
                    </div>
                  </div>

                  {scheduledAttendees.length === 0 ? (
                    <div className="adm-attendance-empty">
                      <Users size={38} color="#94a3b8" />
                      <h4>Belum Ada Calon Siswa Dijadwalkan</h4>
                      <p>
                        Belum ada calon siswa yang ditempatkan pada sesi tes ini oleh Admin Admissions saat tahapan validasi berkas pendaftaran.
                      </p>
                    </div>
                  ) : (
                    <div className="adm-attendance-table-wrap">
                      <table className="adm-attendance-table">
                        <thead>
                          <tr>
                            <th style={{ width: 35, textAlign: 'center' }}>No</th>
                            <th style={{ width: 125 }}>No. Registrasi</th>
                            <th>Nama Lengkap Calon Siswa</th>
                            <th style={{ width: 95, textAlign: 'center' }}>Jenjang</th>
                            <th>Kontak Ortu / WA</th>
                            <th style={{ width: 140, textAlign: 'center' }}>Paraf Presensi</th>
                            <th style={{ width: 100, textAlign: 'center' }}>Catatan</th>
                          </tr>
                        </thead>
                        <tbody>
                          {scheduledAttendees.map((att, idx) => (
                            <tr key={att.id}>
                              <td style={{ textAlign: 'center', fontWeight: 700 }}>{idx + 1}</td>
                              <td className="adm-mono" style={{ fontSize: '0.8rem', fontWeight: 700 }}>{att.registration_number}</td>
                              <td>
                                <strong>{att.child_full_name}</strong>
                                {att.campus_choice && (
                                  <div style={{ fontSize: '0.72rem', color: '#64748b' }}>Kampus: {att.campus_choice}</div>
                                )}
                              </td>
                              <td style={{ textAlign: 'center' }}>
                                <span className="adm-att-level-badge">{att.level_name || att.level_code?.toUpperCase()}</span>
                              </td>
                              <td>
                                <div style={{ fontSize: '0.82rem', fontWeight: 600 }}>{att.parent_name || att.father_name || att.mother_name || '-'}</div>
                                <div style={{ fontSize: '0.74rem', color: '#64748b' }}>{att.parent_phone || att.whatsapp_number || '-'}</div>
                              </td>
                              <td className="adm-att-sign-cell">
                                <div className="adm-sign-box">
                                  <span className="sign-dot"># {idx + 1}</span>
                                </div>
                              </td>
                              <td className="adm-att-note-cell">
                                <div className="adm-note-line"></div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  <div className="adm-attendance-print-footer">
                    <div className="adm-att-sign-col">
                      <p>Pengawas / Guru Penguji Tes:</p>
                      <div className="att-sign-space"></div>
                      <p className="att-sign-name">( .................................................... )</p>
                    </div>
                    <div className="adm-att-sign-col">
                      <p>Petugas Admin Admissions:</p>
                      <div className="att-sign-space"></div>
                      <p className="att-sign-name">( .................................................... )</p>
                    </div>
                  </div>
                </div>

                <div className="adm-config-modal-footer">
                  <button type="button" className="adm-btn-outline" onClick={() => setAttendanceModalSlot(null)}>
                    Tutup
                  </button>
                  <button
                    type="button"
                    className="adm-btn-primary"
                    onClick={() => handlePrintAttendanceSheet(attendanceModalSlot, scheduledAttendees)}
                  >
                    <Printer size={15} />
                    <span>Cetak Lembar Presensi (Print / PDF)</span>
                  </button>
                </div>
              </div>
            </div>
          );
        })()}

        {/* ===================================================================
            OFFICIAL ADMISSIONS DOCUMENTS PREVIEW MODALS
        =================================================================== */}
        {previewTestCardApp && (
          <ExaminationPassModal
            app={previewTestCardApp}
            onClose={() => setPreviewTestCardApp(null)}
          />
        )}

        {previewLoaApp && (
          <LetterOfAcceptanceModal
            app={previewLoaApp}
            onClose={() => setPreviewLoaApp(null)}
          />
        )}

        {previewCertApp && (
          <OfficialEnrollmentCertificateModal
            app={previewCertApp}
            onClose={() => setPreviewCertApp(null)}
          />
        )}

        {previewReceiptApp && (
          <OfficialPaymentReceiptModal
            app={previewReceiptApp}
            onClose={() => setPreviewReceiptApp(null)}
          />
        )}

        {/* Floating Modern Notification Toast */}
        <ModernAdmissionsToast
          toast={feedback}
          onClose={() => setFeedback(null)}
        />
      </div>
      )}
    </DashboardLayout>
  );
}

export default AdmissionsAdminDashboard;
